import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box,
  Heading,
  Button,
  Card,
  CardBody,
  useToast,
  HStack,
  VStack,
  Text,
  Grid,
  GridItem,
  Badge,
  IconButton,
  useDisclosure,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  FormControl,
  FormLabel,
  Input,
  Select,
  NumberInput,
  NumberInputField,
  NumberInputStepper,
  NumberIncrementStepper,
  NumberDecrementStepper,
  Divider,
  AlertDialog,
  AlertDialogBody,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogContent,
  AlertDialogOverlay,
  InputGroup,
  InputLeftElement,
  SimpleGrid,
  Skeleton,
  SkeletonText,
  Checkbox,
  Textarea,
} from '@chakra-ui/react';
import { FiPlus, FiRefreshCw, FiEdit, FiTrash2, FiPackage, FiSearch, FiShare2, FiCopy, FiMail, FiImage, FiSend, FiCheckCircle } from 'react-icons/fi';
import { Icon } from '@chakra-ui/react';
import { packageApi, bpProductNewApi, bpProductRetailApi } from '../config/api';
import StatusChip from '../components/bundling/StatusChip';
import ReadinessPanel from '../components/bundling/ReadinessPanel';
import PriceSummaryBlock from '../components/bundling/PriceSummaryBlock';
import DomainBadge from '../components/bundling/DomainBadge';
import EmptyStateBlock from '../components/bundling/EmptyStateBlock';
import SummaryCardGroup from '../components/bundling/SummaryCardGroup';
import WizardShell from '../components/bundling/WizardShell';
import { renderMiniMarkdown } from '../lib/miniMarkdown.mjs';

// Renders the Markdown subset (bold / lists / line breaks) supported by the
// package Description field. Input is escaped inside renderMiniMarkdown, so the
// HTML injected here is XSS-safe.
const MarkdownText = ({ children, ...props }) => (
  <Box
    fontSize="sm"
    color="gray.600"
    sx={{
      '& p': { m: 0 },
      '& p + p': { mt: 2 },
      '& ul': { pl: 5, listStyleType: 'disc' },
      '& ol': { pl: 5, listStyleType: 'decimal' },
      '& li': { mb: 0.5 },
      '& strong': { fontWeight: 600 },
    }}
    dangerouslySetInnerHTML={{ __html: renderMiniMarkdown(children) }}
    {...props}
  />
);

const MD_HINT = 'Format didukung: **tebal**, baris baru, "- " untuk poin, "1. " untuk nomor.';

const MEMBER_CODE_MAPPING = {
  'api': 'Injourney Airports',
  'ias': 'Injourney Aviation Services',
  'twc': 'Injourney Destination Management',
  'hin': 'Injourney Hospitality',
  'itdc': 'Injourney Tourism Development',
};

const getPriceForItem = (item) => {
  const quantity = Number(item.quantity || item.PACKAGEDETAIL_QUANTITY || 1);
  const priceType = item.priceType || item.PACKAGEDETAIL_PRICETYPE || 'base';

  const publishPrice = Number(item.product?.BPPRODUCT_PUBLISHPRICE || 0);
  const corporatePrice = Number(item.product?.BPPRODUCT_CORPPRICE || publishPrice);
  const agentPrice = Number(item.product?.BPPRODUCT_AGENTPRICE || publishPrice);
  const fallbackBase = Number(item.base_price || item.unit_price || 0);

  const basePrice = publishPrice || fallbackBase;
  let finalPrice = basePrice;

  if (priceType === 'corporate') {
    finalPrice = corporatePrice || basePrice;
  } else if (priceType === 'agent') {
    finalPrice = agentPrice || basePrice;
  }

  return {
    quantity,
    priceType,
    basePrice,
    finalPrice,
    subtotal: finalPrice * quantity,
  };
};

const getPackageSummary = (pkg) => {
  const items = pkg.items || [];

  return items.reduce(
    (acc, item) => {
      const { basePrice, finalPrice, quantity } = getPriceForItem(item);
      acc.totalBasePrice += basePrice * quantity;
      acc.totalFinalPrice += finalPrice * quantity;
      return acc;
    },
    { totalBasePrice: 0, totalFinalPrice: 0 }
  );
};

const getPackageBlockers = (pkg) => {
  const blockers = [];
  if (!pkg.package_name) blockers.push('Nama package belum terisi');
  if (!pkg.valid_date) blockers.push('Start date belum diisi');
  if (!pkg.valid_end_date) blockers.push('End date belum diisi');
  if (!pkg.pic_member) blockers.push('PIC member belum dipilih');
  if (!pkg.quota || Number(pkg.quota) <= 0) blockers.push('Quota belum valid');
  if (!pkg.items || pkg.items.length === 0) blockers.push('Belum ada item di package');
  return blockers;
};

const getVisualStatus = (pkg) => {
  const apiPublished = pkg.PACKAGE_APISTATUS === 'published'
    || pkg.BPPRODUCT_APISTATUS === 'published';

  // If live on API Hub → Published (takes priority)
  if (apiPublished) return 'Published';

  const blockers = getPackageBlockers(pkg);
  const hasItems = (pkg.items || []).length > 0;

  const now = new Date();
  const start = pkg.valid_date ? new Date(pkg.valid_date) : null;
  const end = pkg.valid_end_date ? new Date(pkg.valid_end_date) : null;

  // End date passed → Archived
  if (end && end < now) return 'Archived';

  // Individual product not published → Ready
  if (pkg._isIndividualProduct) return 'Ready';

  // No items at all → Draft
  if (!hasItems) return 'Draft';

  // Quota exhausted (0) while package is otherwise complete → Paused
  if (Number(pkg.quota || 0) === 0 && blockers.length <= 1) return 'Paused';

  // Complete with no blockers → Ready to publish
  if (blockers.length === 0) return 'Ready';

  // Has items but still has blockers → Draft
  return 'Draft';
};

const WIZARD_STEPS = [
  { key: 'basics', label: 'Basics' },
  { key: 'select-products', label: 'Select Products' },
  { key: 'configure', label: 'Configure' },
  { key: 'preview-publish', label: 'Preview & Publish' },
];

const getPackageDashboard = (packageList = []) => {
  return packageList.reduce(
    (summary, pkg) => {
      const status = getVisualStatus(pkg);
      const blockers = getPackageBlockers(pkg);
      summary.total += 1;
      summary.items += pkg.items?.length || 0;
      summary[status] = (summary[status] || 0) + 1;
      summary.blockers += blockers.length;
      return summary;
    },
    { total: 0, items: 0, blockers: 0, Draft: 0, Ready: 0, Published: 0, Paused: 0, Archived: 0, Error: 0 }
  );
};

const MyBundlingPackage = () => {
  const [packages, setPackages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [listSearch, setListSearch] = useState('');
  const [publicationFilter, setPublicationFilter] = useState('all');
  const [readinessFilter, setReadinessFilter] = useState('all');
  const [listPage, setListPage] = useState(1);
  const [selectedPackages, setSelectedPackages] = useState([]);
  const [editingPackage, setEditingPackage] = useState(null);
  const [editingItem, setEditingItem] = useState(null);
  const [deleteItem, setDeleteItem] = useState(null);
  const toast = useToast();
  
  const { isOpen: isFormOpen, onOpen: onFormOpen, onClose: onFormClose } = useDisclosure();
  const { isOpen: isItemEditOpen, onOpen: onItemEditOpen, onClose: onItemEditClose } = useDisclosure();
  const { isOpen: isDeleteItemOpen, onOpen: onDeleteItemOpen, onClose: onDeleteItemClose } = useDisclosure();
  const { isOpen: isAddItemOpen, onOpen: onAddItemOpen, onClose: onAddItemClose } = useDisclosure();
  const { isOpen: isManageImagesOpen, onOpen: onManageImagesOpen, onClose: onManageImagesClose } = useDisclosure();
  const { isOpen: isWizardOpen, onOpen: onWizardOpen, onClose: onWizardClose } = useDisclosure();
  const { isOpen: isWizardExitOpen, onOpen: onWizardExitOpen, onClose: onWizardExitClose } = useDisclosure();
  const cancelRef = React.useRef();
  const wizardExitCancelRef = React.useRef();
  
  const [packageImages, setPackageImages] = useState([]);
  const [uploadingImages, setUploadingImages] = useState(false);

  const [formData, setFormData] = useState({
    package_name: '',
    description: '',
    price_includes: '',
    price_excludes: '',
    valid_date: '',
    valid_end_date: '',
    quota: '',
    pic_member: '',
  });

  const [itemFormData, setItemFormData] = useState({
    quantity: 1,
    unit_price: 0,
  });
  const [itemPriceType, setItemPriceType] = useState('bundle'); // 'bundle' or 'discount'
  const [itemDiscountPercent, setItemDiscountPercent] = useState(0);
  const [inventoryItems, setInventoryItems] = useState([]);
  const [selectedInventoryItem, setSelectedInventoryItem] = useState(null);
  const [addItemQuantity, setAddItemQuantity] = useState(1);
  const [addItemPrice, setAddItemPrice] = useState(0);
  const [addItemPriceType, setAddItemPriceType] = useState('bundle'); // 'bundle' or 'discount'
  const [addItemDiscountPercent, setAddItemDiscountPercent] = useState(0);
  const [addItemSelectedPriceType, setAddItemSelectedPriceType] = useState('base'); // 'base', 'corporate', 'agent'
  const [searchInventoryQuery, setSearchInventoryQuery] = useState('');
  const [wizardStep, setWizardStep] = useState(0);
  const [wizardSaving, setWizardSaving] = useState(false);
  const [wizardDraftId, setWizardDraftId] = useState(null);
  const [wizardDirty, setWizardDirty] = useState(false);
  const [wizardSearchQuery, setWizardSearchQuery] = useState('');
  const [wizardInventoryItems, setWizardInventoryItems] = useState([]);
  const [wizardLoadingInventory, setWizardLoadingInventory] = useState(false);
  const [wizardSelectedInventoryItem, setWizardSelectedInventoryItem] = useState(null);
  const [wizardAddQuantity, setWizardAddQuantity] = useState(1);
  const [wizardAddSelectedPriceType, setWizardAddSelectedPriceType] = useState('base');
  const [wizardForm, setWizardForm] = useState({
    package_name: '',
    description: '',
    price_includes: '',
    price_excludes: '',
    valid_date: '',
    valid_end_date: '',
    quota: '',
    pic_member: '',
  });
  const [wizardSelectedItems, setWizardSelectedItems] = useState([]);
  const [wizardConfig, setWizardConfig] = useState({
    pricingMode: 'inherit',
    discountCapPercent: 0,
    channelHint: 'all-channels',
    notes: '',
  });
  const packageDashboard = useMemo(() => getPackageDashboard(packages), [packages]);

  const resetWizardState = useCallback(() => {
    setEditingPackage(null);
    setWizardStep(0);
    setWizardDraftId(null);
    setWizardSearchQuery('');
    setWizardInventoryItems([]);
    setWizardSelectedInventoryItem(null);
    setWizardAddQuantity(1);
    setWizardAddSelectedPriceType('base');
    setWizardDirty(false);
    setWizardSelectedItems([]);
    setWizardConfig({
      pricingMode: 'inherit',
      discountCapPercent: 0,
      channelHint: 'all-channels',
      notes: '',
    });
  }, []);

  const fetchPackages = useCallback(async () => {
    setLoading(true);
    try {
      const response = await packageApi.get('/list-with-details');
      if (response.data.status === 200) {
        // Transform API data to match existing format
        const transformedPackages = await Promise.all(
          (response.data.data || []).map(async (pkg) => {
            try {
              // Fetch package details
              const details = pkg.items || [];
              
              // Fetch product details for each item
              const itemsWithDetails = await Promise.all(
                details.map(async (detail) => {
                  try {
                    let productData = detail.product || null;
                    if (!productData && detail.BPPRODUCT_ID) {
                      try {
                        const productResponse = await bpProductNewApi.get(`/${detail.BPPRODUCT_ID}`);
                        if (productResponse.data.status === 200) {
                          productData = productResponse.data.data;
                        }
                      } catch (e) {
                        console.error('Error fetching product:', e);
                        productData = null;
                      }
                    } else if (!productData && detail.BPPRODUCTRETAIL_ID) {
                      try {
                        const retailResponse = await bpProductRetailApi.get(`/${detail.BPPRODUCTRETAIL_ID}`);
                        if (retailResponse.data.status === 200) {
                          const retailProduct = retailResponse.data.data;
                          // Get first variant and stock for price
                          const variantsResponse = await bpProductRetailApi.get(`/${detail.BPPRODUCTRETAIL_ID}/variants`);
                          let variant = null;
                          let stockPrice = 0;
                          let stockQty = 0;
                          
                          if (variantsResponse.data.status === 200 && variantsResponse.data.data && variantsResponse.data.data.length > 0) {
                            variant = variantsResponse.data.data[0];
                            const stocksResponse = await bpProductRetailApi.get(`/variant/${variant.BPPRODUCTVARIANT_KEY}/stocks`);
                            if (stocksResponse.data.status === 200 && stocksResponse.data.data && stocksResponse.data.data.length > 0) {
                              const stock = stocksResponse.data.data[0];
                              stockPrice = parseFloat(stock.BPPRODUCTSTOCKS_SELLPRICE) || 0;
                              stockQty = parseInt(stock.BPPRODUCTSTOCKS_STOCK) || 0;
                            }
                          }
                          
                          productData = {
                            BPPRODUCT_ID: retailProduct.BPPRODUCT_ID,
                            BPPRODUCT_NAME: retailProduct.BPPRODUCT_NAME,
                            BPPRODUCT_CODE: retailProduct.BPPRODUCT_KEY,
                            BPPRODUCT_PUBLISHPRICE: stockPrice.toString(),
                            BPPRODUCT_QUANTITY: stockQty.toString(),
                            product_type: 'retail',
                          };
                        }
                      } catch (e) {
                        console.error('Error fetching retail product:', e);
                        productData = { BPPRODUCT_ID: detail.BPPRODUCTRETAIL_ID, product_type: 'retail' };
                      }
                    }
                    
                    return {
                      PACKAGEDETAIL_ID: detail.PACKAGEDETAIL_ID,
                      BPPRODUCT_ID: detail.BPPRODUCT_ID,
                      BPPRODUCTRETAIL_ID: detail.BPPRODUCTRETAIL_ID,
                      quantity: detail.PACKAGEDETAIL_QUANTITY || 1,
                      priceType: detail.PACKAGEDETAIL_PRICETYPE || 'base',
                      product: productData,
                    };
                  } catch (e) {
                    console.error('Error processing detail:', e);
                    return {
                      PACKAGEDETAIL_ID: detail.PACKAGEDETAIL_ID,
                      BPPRODUCT_ID: detail.BPPRODUCT_ID,
                      BPPRODUCTRETAIL_ID: detail.BPPRODUCTRETAIL_ID,
                      product: null,
                    };
                  }
                })
              );
              
              return {
                id: pkg.PACKAGE_ID,
                PACKAGE_ID: pkg.PACKAGE_ID,
                PACKAGE_CODE: pkg.PACKAGE_CODE,
                package_name: pkg.PACKAGE_NAME,
                description: pkg.PACKAGE_DESC,
                price_includes: pkg.PACKAGE_PRICEINCLUDES,
                price_excludes: pkg.PACKAGE_PRICEEXCLUDES,
                valid_date: pkg.PACKAGE_VALIDSTARTDATE,
                valid_end_date: pkg.PACKAGE_VALIDENDDATE,
                quota: pkg.PACKAGE_KUOTA,
                pic_member: pkg.PACKAGE_PICMEMBER,
                items: itemsWithDetails,
                PACKAGE_BASEPRICE: pkg.PACKAGE_BASEPRICE,
                PACKAGE_DISCOUNTPRICE: pkg.PACKAGE_DISCOUNTPRICE,
                PACKAGE_APISTATUS: pkg.PACKAGE_APISTATUS || 'draft',
                total_price: parseFloat(pkg.PACKAGE_DISCOUNTPRICE || pkg.PACKAGE_BASEPRICE) || 0,
                created_at: pkg.CREATED_TIME,
              };
            } catch (e) {
              console.error('Error processing package:', e);
              // Return package with empty items if there's an error
              return {
                id: pkg.PACKAGE_ID,
                PACKAGE_ID: pkg.PACKAGE_ID,
                PACKAGE_CODE: pkg.PACKAGE_CODE,
                package_name: pkg.PACKAGE_NAME,
                description: pkg.PACKAGE_DESC,
                price_includes: pkg.PACKAGE_PRICEINCLUDES,
                price_excludes: pkg.PACKAGE_PRICEEXCLUDES,
                valid_date: pkg.PACKAGE_VALIDSTARTDATE,
                valid_end_date: pkg.PACKAGE_VALIDENDDATE,
                quota: pkg.PACKAGE_KUOTA,
                PACKAGE_BASEPRICE: pkg.PACKAGE_BASEPRICE,
                PACKAGE_DISCOUNTPRICE: pkg.PACKAGE_DISCOUNTPRICE,
                PACKAGE_APISTATUS: pkg.PACKAGE_APISTATUS || 'draft',
                pic_member: pkg.PACKAGE_PICMEMBER,
                items: [],
                total_price: parseFloat(pkg.PACKAGE_BASEPRICE) || 0,
                created_at: pkg.CREATED_TIME,
              };
            }
          })
        );
        // Fetch published individual products and merge
        try {
          const prodRes = await bpProductNewApi.get('/browse');
          if (prodRes.data?.status === 200) {
            const publishedProducts = (prodRes.data.data || [])
              .filter(p => p.BPPRODUCT_APISTATUS === 'published')
              .map(p => {
                const prodPrice = parseFloat(p.BPPRODUCT_PUBLISHPRICE || p.BPPRODUCT_CORPPRICE || p.BPPRODUCT_AGENTPRICE) || 0;
                const today = new Date().toISOString().split('T')[0];
                return {
                  id: `prod-${p.BPPRODUCT_ID}`,
                  PACKAGE_ID: null,
                  PACKAGE_CODE: p.BPPRODUCT_CODE,
                  package_name: p.BPPRODUCT_NAME,
                  description: p.BPPRODUCT_DESC || '',
                  valid_date: p.BPPRODUCT_STARTDATE || today,
                  valid_end_date: p.BPPRODUCT_ENDDATE || today,
                  quota: parseInt(p.BPPRODUCT_QUANTITY) || 1,
                  pic_member: p.MEMBER_CODE || '',
                  items: [{ name: p.BPPRODUCT_NAME, category: p.BPPRODUCT_CATEGORY || 'Individual Product', quantity: 1 }],
                  PACKAGE_BASEPRICE: p.BPPRODUCT_PUBLISHPRICE,
                  PACKAGE_DISCOUNTPRICE: p.BPPRODUCT_CORPPRICE,
                  PACKAGE_APISTATUS: p.BPPRODUCT_APISTATUS || 'draft',
                  total_price: prodPrice,
                  created_at: p.CREATED_TIME,
                  _isIndividualProduct: true,
                };
              });
            setPackages([...transformedPackages, ...publishedProducts]);
          } else {
            setPackages(transformedPackages);
          }
        } catch (prodErr) {
          console.warn('Could not fetch individual products:', prodErr.message);
          setPackages(transformedPackages);
        }
      } else {
        setPackages([]);
      }
    } catch (error) {
      console.error('Error fetching packages:', error);
      console.error('Error stack:', error.stack);
      toast({
        title: 'Error',
        description: error.response?.data?.message || error.message || 'Terjadi kesalahan saat mengambil data packages',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      setPackages([]);
    } finally {
      setLoading(false);
    }
  }, [toast]);


  useEffect(() => {
    fetchPackages();
  }, [fetchPackages]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleCreate = useCallback((templateDraft = null) => {
    resetWizardState();
    setWizardForm({
      package_name: templateDraft?.title ? `${templateDraft.title} ${new Date().toISOString().slice(0, 10)}` : '',
      description: templateDraft?.defaults?.description || '',
      valid_date: '',
      valid_end_date: '',
      quota: templateDraft?.defaults?.quota || '',
      pic_member: '',
    });
    setWizardConfig({
      pricingMode: 'inherit',
      discountCapPercent: 0,
      channelHint: templateDraft?.defaults?.channelHint || 'all-channels',
      notes: '',
    });
    onWizardOpen();
  }, [onWizardOpen, resetWizardState]);

  useEffect(() => {
    const rawTemplate = localStorage.getItem('packageTemplateDraft');
    if (!rawTemplate) return;
    try {
      const templateDraft = JSON.parse(rawTemplate);
      if (templateDraft?.title) {
        handleCreate(templateDraft);
      }
    } catch (error) {
      console.error('Error parsing package template draft:', error);
    } finally {
      localStorage.removeItem('packageTemplateDraft');
    }
  }, [handleCreate]);

  const handleWizardResume = (pkg) => {
    if (pkg._isIndividualProduct) {
      window.location.assign('/admin/package-manager/product-catalog');
      return;
    }
    setEditingPackage(pkg);
    setWizardStep(0);
    setWizardDraftId(pkg.PACKAGE_ID || pkg.id);
    setWizardSearchQuery('');
    setWizardInventoryItems([]);
    setWizardSelectedInventoryItem(null);
    setWizardAddQuantity(1);
    setWizardAddSelectedPriceType('base');
    setWizardDirty(false);
    setWizardForm({
      package_name: pkg.package_name || '',
      description: pkg.description || '',
      price_includes: pkg.price_includes || '',
      price_excludes: pkg.price_excludes || '',
      valid_date: pkg.valid_date || '',
      valid_end_date: pkg.valid_end_date || '',
      quota: pkg.quota || '',
      pic_member: pkg.pic_member || pkg.PACKAGE_PICMEMBER || '',
    });
    setWizardSelectedItems(pkg.items || []);
    // Restore wizard config from localStorage if available
    const pkgId = pkg.PACKAGE_ID || pkg.id;
    let restoredConfig = { pricingMode: 'inherit', discountCapPercent: 0, channelHint: 'all-channels', notes: '' };
    try {
      const saved = localStorage.getItem(`wizardConfig_${pkgId}`);
      if (saved) {
        restoredConfig = { ...restoredConfig, ...JSON.parse(saved) };
      }
    } catch { /* use defaults */ }
    setWizardConfig(restoredConfig);
    onWizardOpen();
  };

  // Only sends populated fields — prevents overwriting existing DB values with null/empty
  const updatePackageBasics = useCallback(async (packageId, payload) => {
    const body = { PACKAGE_STATUS: '1' };
    if (payload.package_name?.trim())        body.PACKAGE_NAME = payload.package_name.trim();
    if (payload.description?.trim())         body.PACKAGE_DESC = payload.description.trim();
    if (payload.price_includes?.trim())      body.PACKAGE_PRICEINCLUDES = payload.price_includes.trim();
    if (payload.price_excludes?.trim())      body.PACKAGE_PRICEEXCLUDES = payload.price_excludes.trim();
    if (payload.valid_date)                  body.PACKAGE_VALIDSTARTDATE = payload.valid_date;
    if (payload.valid_end_date)              body.PACKAGE_VALIDENDDATE = payload.valid_end_date;
    if (payload.quota)                       body.PACKAGE_KUOTA = payload.quota;
    if (payload.pic_member)                  body.PACKAGE_PICMEMBER = payload.pic_member;
    return packageApi.post(`/update/${packageId}`, body);
  }, []);

  const ensureWizardDraft = useCallback(async () => {
    if (!wizardForm.package_name?.trim()) {
      throw new Error('Nama package wajib diisi');
    }

    if (wizardDraftId) {
      if (wizardDirty) await updatePackageBasics(wizardDraftId, wizardForm);
      return wizardDraftId;
    }

    const response = await packageApi.post('/', {
      PACKAGE_NAME: wizardForm.package_name,
      PACKAGE_DESC: wizardForm.description || '',
      PACKAGE_PRICEINCLUDES: wizardForm.price_includes || '',
      PACKAGE_PRICEEXCLUDES: wizardForm.price_excludes || '',
      PACKAGE_VALIDSTARTDATE: wizardForm.valid_date || null,
      PACKAGE_VALIDENDDATE: wizardForm.valid_end_date || null,
      PACKAGE_KUOTA: wizardForm.quota || null,
      PACKAGE_PICMEMBER: wizardForm.pic_member || null,
      PACKAGE_STATUS: '1',
      PACKAGE_BASEPRICE: null,
      PACKAGE_DISCOUNTPRICE: null,
      items: [],
    });

    if (response.data.status !== 201 || !response.data.data?.PACKAGE_ID) {
      throw new Error(response.data?.message || 'Gagal membuat draft package');
    }

    const newId = response.data.data.PACKAGE_ID;
    setWizardDraftId(newId);
    return newId;
  }, [updatePackageBasics, wizardDraftId, wizardDirty, wizardForm]);

  const mapWizardInventoryItems = useCallback((products) => {
    return (products || []).map((product) => {
      const location = product.product_type === 'retail'
        ? 'Injourney Retail'
        : (product.COMPANY_NAME || MEMBER_CODE_MAPPING[product.MEMBER_CODE?.toLowerCase()] || product.MEMBER_CODE || '-');
      return {
        id: product.BPPRODUCT_ID,
        name: product.BPPRODUCT_NAME || '-',
        description: product.BPPRODUCT_DESC || '-',
        location,
        price: parseFloat(product.BPPRODUCT_PUBLISHPRICE) || 0,
        quantity: parseInt(product.BPPRODUCT_QUANTITY || 0, 10),
        productCode: product.BPPRODUCT_CODE || '-',
        productType: product.product_type || 'product',
        memberCode: product.MEMBER_CODE || '',
        corporatePrice: parseFloat(product.BPPRODUCT_CORPPRICE) || null,
        agentPrice: parseFloat(product.BPPRODUCT_AGENTPRICE) || null,
        apiStatus: product.BPPRODUCT_APISTATUS || 'draft',
      };
    });
  }, []);

  const loadWizardInventoryItems = useCallback(async () => {
    setWizardLoadingInventory(true);
    try {
      const params = {};
      if (wizardSearchQuery) params.search = wizardSearchQuery;
      const response = await bpProductNewApi.get('/browse', { params });
      if (response.data.status === 200) {
        setWizardInventoryItems(mapWizardInventoryItems(response.data.data || []));
      } else {
        setWizardInventoryItems([]);
      }
    } catch (error) {
      console.error('Error fetching wizard inventory:', error);
      setWizardInventoryItems([]);
    } finally {
      setWizardLoadingInventory(false);
    }
  }, [wizardSearchQuery, mapWizardInventoryItems]);

  const handleEdit = (pkg) => {
    setEditingPackage(pkg);
    setFormData({
      package_name: pkg.package_name || '',
      description: pkg.description || '',
      price_includes: pkg.price_includes || '',
      price_excludes: pkg.price_excludes || '',
      valid_date: pkg.valid_date || '',
      valid_end_date: pkg.valid_end_date || '',
      quota: pkg.quota || '',
      pic_member: pkg.pic_member || pkg.PACKAGE_PICMEMBER || '',
    });
    onFormOpen();
  };

  useEffect(() => {
    if (!isWizardOpen || wizardStep !== 1) return;
    loadWizardInventoryItems();
  }, [isWizardOpen, wizardStep, wizardSearchQuery, loadWizardInventoryItems]);

  useEffect(() => {
    if (!isWizardOpen || !wizardDraftId || wizardStep !== 0 || !wizardDirty) return undefined;
    const timer = setTimeout(async () => {
      try {
        await updatePackageBasics(wizardDraftId, wizardForm);
        setWizardDirty(false);
      } catch (error) {
        console.error('Autosave draft failed:', error);
      }
    }, 900);
    return () => clearTimeout(timer);
  }, [isWizardOpen, wizardDraftId, wizardStep, wizardDirty, wizardForm, updatePackageBasics]);

  const handleWizardFormChange = (name, value) => {
    setWizardForm((prev) => ({ ...prev, [name]: value }));
    setWizardDirty(true);
  };

  const handleWizardConfigChange = (patch) => {
    setWizardConfig((prev) => {
      const next = { ...prev, ...patch };
      // Persist to localStorage so config survives page navigation
      if (wizardDraftId) {
        try {
          localStorage.setItem(`wizardConfig_${wizardDraftId}`, JSON.stringify(next));
        } catch { /* ignore */ }
      }
      return next;
    });
    setWizardDirty(true);
  };

  const handleWizardNext = async () => {
    if (wizardStep === 0 && !wizardForm.package_name?.trim()) {
      toast({
        title: 'Validasi gagal',
        description: 'Nama package wajib diisi',
        status: 'warning',
        duration: 2500,
        isClosable: true,
      });
      return;
    }
    if (wizardStep === 1 && wizardSelectedItems.length === 0 && !wizardSelectedInventoryItem) {
      toast({
        title: 'Produk belum dipilih',
        description: 'Pilih minimal satu produk atau tambahkan ke selected panel sebelum lanjut.',
        status: 'warning',
        duration: 2500,
        isClosable: true,
      });
      return;
    }
    if (wizardStep === 3) {
      const blockers = getWizardPublishBlockers();
      if (blockers.length > 0) {
        toast({
          title: 'Publish diblokir',
          description: 'Masih ada blocker readiness. Cek panel blocker di Step 4.',
          status: 'error',
          duration: 2800,
          isClosable: true,
        });
        return;
      }
      setWizardSaving(true);
      try {
        const draftId = await ensureWizardDraft();
        await updatePackageBasics(draftId, wizardForm);
        // Actually call publish endpoint with config payload
        const res = await packageApi.post(`/publish/${draftId}`, {
          pricingMode: wizardConfig.pricingMode,
          discountCapPercent: wizardConfig.discountCapPercent,
          channelHint: wizardConfig.channelHint,
          notes: wizardConfig.notes,
        });
        if (res.data?.status === 200) {
          toast({
            title: 'Package Published!',
            description: res.data.message || 'Package berhasil dipublish ke API Hub.',
            status: 'success',
            duration: 3000,
            isClosable: true,
          });
          await closeWizardSafely();
        }
      } catch (error) {
        toast({
          title: 'Gagal publish',
          description: error.response?.data?.message || error.message || 'Terjadi kesalahan saat publish.',
          status: 'error',
          duration: 3000,
          isClosable: true,
        });
      } finally {
        setWizardSaving(false);
      }
      return;
    }

    setWizardSaving(true);
    try {
      const draftId = await ensureWizardDraft();
      setWizardStep((prev) => Math.min(prev + 1, WIZARD_STEPS.length - 1));
      setWizardDraftId(draftId);
      toast({
        title: 'Draft tersimpan',
        description: wizardStep === 0 ? 'Lanjut ke pemilihan produk.' : wizardStep === 1 ? 'Lanjut ke konfigurasi komersial.' : 'Lanjut ke final review.',
        status: 'success',
        duration: 1800,
        isClosable: true,
      });
    } catch (error) {
      toast({
        title: 'Gagal melanjutkan wizard',
        description: error.response?.data?.message || error.message || 'Terjadi kesalahan saat menyimpan draft',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setWizardSaving(false);
    }
  };

  const handleWizardBack = () => {
    setWizardStep((prev) => Math.max(prev - 1, 0));
  };

  const handleWizardSaveDraft = async () => {
    setWizardSaving(true);
    try {
      await ensureWizardDraft();
      setWizardDirty(false);
      await fetchPackages();
      toast({
        title: 'Draft tersimpan',
        description: 'Package draft bisa dilanjutkan kapan saja',
        status: 'success',
        duration: 2200,
        isClosable: true,
      });
    } catch (error) {
      toast({
        title: 'Gagal menyimpan draft',
        description: error.message || error.response?.data?.message || 'Terjadi kesalahan',
        status: 'error',
        duration: 2800,
        isClosable: true,
      });
    } finally {
      setWizardSaving(false);
    }
  };

  const closeWizardSafely = async () => {
    onWizardClose();
    onWizardExitClose();
    setWizardStep(0);
    setWizardSearchQuery('');
    setWizardSelectedInventoryItem(null);
    setWizardInventoryItems([]);
    setWizardConfig({
      pricingMode: 'inherit',
      discountCapPercent: 0,
      channelHint: 'all-channels',
      notes: '',
    });
    setWizardDirty(false);
    // Cleanup saved wizard config
    if (wizardDraftId) {
      try { localStorage.removeItem(`wizardConfig_${wizardDraftId}`); } catch { /* ignore */ }
    }
    await fetchPackages();
  };

  const handleWizardRequestClose = () => {
    if (wizardDirty) {
      onWizardExitOpen();
      return;
    }
    closeWizardSafely();
  };

  const handleWizardSelectInventoryItem = (item) => {
    setWizardSelectedInventoryItem(item);
    setWizardAddQuantity(1);
    setWizardAddSelectedPriceType('base');
  };

  const getWizardSelectedPrice = (item) => {
    if (!item) return 0;
    if (item.productType === 'retail') {
      return item.price || 0;
    }
    if (wizardAddSelectedPriceType === 'corporate' && item.corporatePrice) return item.corporatePrice;
    if (wizardAddSelectedPriceType === 'agent' && item.agentPrice) return item.agentPrice;
    return item.price || 0;
  };

  const getWizardPreviewSummary = useCallback(() => {
    return wizardSelectedItems.reduce(
      (acc, item) => {
        const priceData = getPriceForItem(item);
        acc.totalBasePrice += (priceData.basePrice || 0) * (priceData.quantity || 1);
        acc.totalFinalPrice += (priceData.finalPrice || 0) * (priceData.quantity || 1);
        return acc;
      },
      { totalBasePrice: 0, totalFinalPrice: 0 }
    );
  }, [wizardSelectedItems]);

  const getWizardPublishBlockers = useCallback(() => {
    const blockers = [];
    if (!wizardForm.package_name?.trim()) {
      blockers.push('Nama package belum terisi. Isi field Package Name di step Basics.');
    }
    if (!wizardForm.valid_date || !wizardForm.valid_end_date) {
      blockers.push('Periode validitas belum lengkap. Isi start date dan end date.');
    }
    if (!wizardForm.quota || Number(wizardForm.quota) <= 0) {
      blockers.push('Quota belum valid. Set quota lebih dari 0.');
    }
    if (!wizardForm.pic_member) {
      blockers.push('PIC member belum dipilih. Pilih member di step Basics.');
    }
    if (!wizardSelectedItems.length) {
      blockers.push('Belum ada produk dipilih. Tambahkan produk di step Select Products.');
    }
    // Check if all selected products are published to API
    const unpublishedProducts = wizardSelectedItems.filter(
      (item) => !item.product || item.product.BPPRODUCT_APISTATUS !== 'published'
    );
    if (unpublishedProducts.length > 0) {
      blockers.push(`${unpublishedProducts.length} produk belum dipublish ke API. Publish masing-masing produk terlebih dahulu.`);
    }
    return blockers;
  }, [wizardForm.package_name, wizardForm.quota, wizardForm.valid_date, wizardForm.valid_end_date, wizardForm.pic_member, wizardSelectedItems]);

  const handleWizardAddProduct = async () => {
    if (!wizardSelectedInventoryItem) return;
    setWizardSaving(true);
    try {
      const draftId = await ensureWizardDraft();
      const requestData = {
        PACKAGEDETAIL_STATUS: '1',
        PACKAGEDETAIL_QUANTITY: wizardAddQuantity || 1,
        PACKAGEDETAIL_PRICETYPE: wizardSelectedInventoryItem.productType === 'retail' ? 'base' : wizardAddSelectedPriceType,
        BPPRODUCT_ID: wizardSelectedInventoryItem.productType === 'retail' ? null : wizardSelectedInventoryItem.id,
        BPPRODUCTRETAIL_ID: wizardSelectedInventoryItem.productType === 'retail' ? wizardSelectedInventoryItem.id : null,
      };

      const response = await packageApi.post(`/${draftId}/add-item`, requestData);
      if (response.data.status === 201) {
        const detail = response.data.data || {};
        setWizardSelectedItems((prev) => [
          ...prev,
          {
            PACKAGEDETAIL_ID: detail.PACKAGEDETAIL_ID || `tmp-${Date.now()}`,
            quantity: requestData.PACKAGEDETAIL_QUANTITY,
            priceType: requestData.PACKAGEDETAIL_PRICETYPE,
            product: {
              BPPRODUCT_NAME: wizardSelectedInventoryItem.name,
              BPPRODUCT_CODE: wizardSelectedInventoryItem.productCode,
              MEMBER_CODE: wizardSelectedInventoryItem.memberCode,
              BPPRODUCT_APISTATUS: wizardSelectedInventoryItem.apiStatus || 'draft',
              BPPRODUCT_PUBLISHPRICE: String(wizardSelectedInventoryItem.price || 0),
              BPPRODUCT_CORPPRICE: wizardSelectedInventoryItem.corporatePrice ? String(wizardSelectedInventoryItem.corporatePrice) : null,
              BPPRODUCT_AGENTPRICE: wizardSelectedInventoryItem.agentPrice ? String(wizardSelectedInventoryItem.agentPrice) : null,
            },
            ...detail,
          },
        ]);
        setWizardSelectedInventoryItem(null);
        setWizardDirty(true);
        await fetchPackages();
      }
    } catch (error) {
      toast({
        title: 'Gagal menambahkan produk',
        description: error.response?.data?.message || 'Terjadi kesalahan saat menambahkan produk',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setWizardSaving(false);
    }
  };

  const handleWizardRemoveProduct = async (item) => {
    if (!item?.PACKAGEDETAIL_ID) return;
    // Item belum di-persist ke backend (fallback ID saat API tidak mengembalikan ID)
    if (String(item.PACKAGEDETAIL_ID).startsWith('tmp-')) {
      setWizardSelectedItems((prev) => prev.filter((row) => row.PACKAGEDETAIL_ID !== item.PACKAGEDETAIL_ID));
      return;
    }
    if (!wizardDraftId) return;
    setWizardSaving(true);
    try {
      const response = await packageApi.post(`/detail/delete/${item.PACKAGEDETAIL_ID}`);
      if (response.data.status === 200) {
        setWizardSelectedItems((prev) => prev.filter((row) => row.PACKAGEDETAIL_ID !== item.PACKAGEDETAIL_ID));
        setWizardDirty(true);
        await fetchPackages();
      }
    } catch (error) {
      toast({
        title: 'Gagal menghapus item',
        description: error.response?.data?.message || 'Terjadi kesalahan saat menghapus item',
        status: 'error',
        duration: 2800,
        isClosable: true,
      });
    } finally {
      setWizardSaving(false);
    }
  };

  const handleSubmit = async () => {
    if (!formData.package_name) {
      toast({
        title: 'Validasi gagal',
        description: 'Package name harus diisi',
        status: 'warning',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    try {
      if (editingPackage) {
        // Update existing package
        const response = await packageApi.post(`/update/${editingPackage.PACKAGE_ID || editingPackage.id}`, {
          PACKAGE_NAME: formData.package_name,
          PACKAGE_DESC: formData.description || '',
          PACKAGE_PRICEINCLUDES: formData.price_includes || '',
          PACKAGE_PRICEEXCLUDES: formData.price_excludes || '',
          PACKAGE_VALIDSTARTDATE: formData.valid_date || null,
          PACKAGE_VALIDENDDATE: formData.valid_end_date || null,
          PACKAGE_KUOTA: formData.quota || null,
          PACKAGE_PICMEMBER: formData.pic_member || null,
          PACKAGE_STATUS: '1',
        });

        if (response.data.status === 200) {
          toast({
            title: 'Berhasil',
            description: 'Package berhasil diupdate',
            status: 'success',
            duration: 3000,
            isClosable: true,
          });
          onFormClose();
          await fetchPackages();
        }
      } else {
        // Create new package
        const response = await packageApi.post('/', {
          PACKAGE_NAME: formData.package_name,
          PACKAGE_DESC: formData.description || '',
          PACKAGE_PRICEINCLUDES: formData.price_includes || '',
          PACKAGE_PRICEEXCLUDES: formData.price_excludes || '',
          PACKAGE_VALIDSTARTDATE: formData.valid_date || null,
          PACKAGE_VALIDENDDATE: formData.valid_end_date || null,
          PACKAGE_KUOTA: formData.quota || null,
          PACKAGE_PICMEMBER: formData.pic_member || null,
          PACKAGE_STATUS: '1',
          PACKAGE_BASEPRICE: null,
          PACKAGE_DISCOUNTPRICE: null,
          items: []
        });

        if (response.data.status === 201) {
          toast({
            title: 'Berhasil',
            description: 'Package berhasil dibuat',
            status: 'success',
            duration: 3000,
            isClosable: true,
          });
          onFormClose();
          await fetchPackages();
        }
      }
    } catch (error) {
      console.error('Error saving package:', error);
      toast({
        title: 'Error',
        description: error.response?.data?.message || 'Terjadi kesalahan saat menyimpan data',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    }
  };

  const handleDelete = async (pkg) => {
    if (!window.confirm(`Apakah Anda yakin ingin menghapus package "${pkg.package_name}"?`)) {
      return;
    }

    try {
      const packageId = pkg.PACKAGE_ID || pkg.id;
      const response = await packageApi.post(`/delete/${packageId}`);
      
      if (response.data.status === 200) {
        toast({
          title: 'Berhasil',
          description: 'Package berhasil dihapus',
          status: 'success',
          duration: 3000,
          isClosable: true,
        });
        await fetchPackages();
      }
    } catch (error) {
      console.error('Error deleting package:', error);
      toast({
        title: 'Error',
        description: error.response?.data?.message || 'Terjadi kesalahan saat menghapus data',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    }
  };

  // Handle edit item
  const handleEditItem = (pkg, item) => {
    setEditingPackage(pkg);
    setEditingItem(item);
    const basePrice = item.base_price || item.unit_price || 0;
    const hasDiscount = item.discount_percent !== null && item.discount_percent !== undefined;
    const hasBundlePrice = item.bundle_price !== null && item.bundle_price !== undefined && item.bundle_price !== basePrice;
    
    setItemPriceType(hasDiscount ? 'discount' : 'bundle');
    setItemDiscountPercent(hasDiscount ? item.discount_percent : 0);
    setItemFormData({
      quantity: item.quantity,
      unit_price: item.unit_price,
    });
    onItemEditOpen();
  };

  // Handle save item edit
  const handleSaveItemEdit = async () => {
    if (!editingPackage || !editingItem) return;

    // Note: Item edit functionality may need API endpoint for updating package detail
    // For now, we'll just refresh the packages
    toast({
      title: 'Info',
      description: 'Fitur edit item sedang dalam pengembangan. Silakan hapus dan tambahkan ulang item.',
      status: 'info',
      duration: 3000,
      isClosable: true,
    });

    onItemEditClose();
    setEditingItem(null);
    await fetchPackages();
  };

  // Handle delete item
  const handleDeleteItem = async () => {
    if (!deleteItem || !editingPackage) return;

    try {
      const detailId = deleteItem.PACKAGEDETAIL_ID;
      if (!detailId) {
        toast({
          title: 'Error',
          description: 'Detail ID tidak ditemukan',
          status: 'error',
          duration: 3000,
          isClosable: true,
        });
        return;
      }

      const response = await packageApi.post(`/detail/delete/${detailId}`);
      
      if (response.data.status === 200) {
        toast({
          title: 'Berhasil',
          description: 'Item berhasil dihapus dari package',
          status: 'success',
          duration: 3000,
          isClosable: true,
        });

        onDeleteItemClose();
        setDeleteItem(null);
        await fetchPackages();
      }
    } catch (error) {
      console.error('Error deleting item:', error);
      toast({
        title: 'Error',
        description: error.response?.data?.message || 'Terjadi kesalahan saat menghapus item',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    }
  };

  // Handle adjust package price
  const handleAdjustPrice = async (pkg, newPrice) => {
    try {
      const packageId = pkg.PACKAGE_ID || pkg.id;
      const response = await packageApi.post(`/update/${packageId}`, {
        PACKAGE_BASEPRICE: newPrice.toString(),
      });

      if (response.data.status === 200) {
        toast({
          title: 'Berhasil',
          description: 'Harga package berhasil diadjust',
          status: 'success',
          duration: 3000,
          isClosable: true,
        });
        await fetchPackages();
      }
    } catch (error) {
      console.error('Error adjusting package price:', error);
      toast({
        title: 'Error',
        description: error.response?.data?.message || 'Terjadi kesalahan saat mengupdate harga package',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    }
  };

  // Handle manage images
  const handleManageImages = async (pkg) => {
    setEditingPackage(pkg);
    try {
      const response = await packageApi.get(`/${pkg.PACKAGE_ID || pkg.id}/images`);
      if (response.data.status === 200) {
        setPackageImages(response.data.data || []);
      }
    } catch (error) {
      console.error('Error fetching package images:', error);
      setPackageImages([]);
    }
    onManageImagesOpen();
  };

  // Handle upload images
  const handleImageUpload = async (event) => {
    if (!editingPackage) return;
    
    const files = Array.from(event.target.files);
    if (files.length === 0) return;

    setUploadingImages(true);
    try {
      const packageId = editingPackage.PACKAGE_ID || editingPackage.id;
      
      for (const file of files) {
        const formData = new FormData();
        formData.append('PACKAGEIMAGE_FILE', file);
        formData.append('PACKAGEIMAGE_NAME', file.name);
        
        await packageApi.post(`/${packageId}/images`, formData, {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        });
      }
      
      // Refresh images
      const response = await packageApi.get(`/${packageId}/images`);
      if (response.data.status === 200) {
        setPackageImages(response.data.data || []);
      }
      
      toast({
        title: 'Berhasil',
        description: 'Images berhasil diupload',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
    } catch (error) {
      console.error('Error uploading images:', error);
      toast({
        title: 'Error',
        description: error.response?.data?.message || 'Gagal mengupload images',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setUploadingImages(false);
    }
  };

  // Handle delete image
  const handleDeleteImage = async (imageId) => {
    try {
      await packageApi.post(`/image/delete/${imageId}`);
      
      // Refresh images
      const packageId = editingPackage?.PACKAGE_ID || editingPackage?.id;
      if (packageId) {
        const response = await packageApi.get(`/${packageId}/images`);
        if (response.data.status === 200) {
          setPackageImages(response.data.data || []);
        }
      }
      
      toast({
        title: 'Berhasil',
        description: 'Image berhasil dihapus',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
    } catch (error) {
      console.error('Error deleting image:', error);
      toast({
        title: 'Error',
        description: error.response?.data?.message || 'Gagal menghapus image',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    }
  };

  // Handle share package
  const handleShare = async (pkg) => {
    try {
      // Use shared getPriceForItem helper to respect priceType
      const totalFromItems = pkg.items && pkg.items.length > 0
        ? pkg.items.reduce((sum, item) => sum + getPriceForItem(item).subtotal, 0)
        : 0;

      const itemsText = pkg.items && pkg.items.length > 0
        ? pkg.items.map((item, index) => {
            const productName = item.name || item.product?.BPPRODUCT_NAME || 'Product';
            const { quantity, finalPrice, priceType } = getPriceForItem(item);
            const priceLabel = priceType === 'corporate' ? ' [Corp]' : priceType === 'agent' ? ' [Eco]' : '';
            return `- ${productName} (Qty: ${quantity})${priceLabel} - Rp ${(finalPrice * quantity).toLocaleString('id-ID')}`;
          }).join('\n')
        : 'No items';

      const shareText = `Package: ${pkg.package_name}\n${pkg.description ? `Description: ${pkg.description}\n` : ''}${pkg.valid_date ? `Start Date: ${new Date(pkg.valid_date).toLocaleDateString('id-ID')}\n` : ''}${pkg.valid_end_date ? `End Date: ${new Date(pkg.valid_end_date).toLocaleDateString('id-ID')}\n` : ''}${pkg.quota ? `Quota: ${pkg.quota}\n` : ''}\nItems:\n${itemsText}\n\nTotal Price: Rp ${totalFromItems.toLocaleString('id-ID')}`;

      if (navigator.share) {
        await navigator.share({
          title: pkg.package_name,
          text: shareText,
        });
      } else {
        // Fallback: copy to clipboard
        await navigator.clipboard.writeText(shareText);
        toast({
          title: 'Berhasil',
          description: 'Package info telah disalin ke clipboard',
          status: 'success',
          duration: 3000,
          isClosable: true,
        });
      }
    } catch (error) {
      if (error.name !== 'AbortError') {
        console.error('Error sharing:', error);
        toast({
          title: 'Error',
          description: 'Gagal membagikan package',
          status: 'error',
          duration: 3000,
          isClosable: true,
        });
      }
    }
  };

  // Handle copy to clipboard
  const handleCopyToClipboard = async (pkg) => {
    try {
      // Use shared getPriceForItem helper to respect priceType
      const totalFromItems = pkg.items && pkg.items.length > 0
        ? pkg.items.reduce((sum, item) => sum + getPriceForItem(item).subtotal, 0)
        : 0;

      const itemsText = pkg.items && pkg.items.length > 0
        ? pkg.items.map((item) => {
            const productName = item.name || item.product?.BPPRODUCT_NAME || 'Product';
            const { quantity, finalPrice, priceType } = getPriceForItem(item);
            const priceLabel = priceType === 'corporate' ? ' [Corp]' : priceType === 'agent' ? ' [Eco]' : '';
            return `- ${productName} (Qty: ${quantity})${priceLabel} - Rp ${(finalPrice * quantity).toLocaleString('id-ID')}`;
          }).join('\n')
        : 'No items';

      const shareText = `Package: ${pkg.package_name}\n${pkg.description ? `Description: ${pkg.description}\n` : ''}${pkg.valid_date ? `Start Date: ${new Date(pkg.valid_date).toLocaleDateString('id-ID')}\n` : ''}${pkg.valid_end_date ? `End Date: ${new Date(pkg.valid_end_date).toLocaleDateString('id-ID')}\n` : ''}${pkg.quota ? `Quota: ${pkg.quota}\n` : ''}\nItems:\n${itemsText}\n\nTotal Price: Rp ${totalFromItems.toLocaleString('id-ID')}`;

      await navigator.clipboard.writeText(shareText);
      
      toast({
        title: 'Berhasil',
        description: 'Package info telah disalin ke clipboard',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
    } catch (error) {
      console.error('Error copying to clipboard:', error);
      toast({
        title: 'Error',
        description: 'Gagal menyalin ke clipboard',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    }
  };

  // Handle email package
  const handleEmail = (pkg) => {
    try {
      // Use shared getPriceForItem helper to respect priceType
      const totalFromItems = pkg.items && pkg.items.length > 0
        ? pkg.items.reduce((sum, item) => sum + getPriceForItem(item).subtotal, 0)
        : 0;

      const itemsText = pkg.items && pkg.items.length > 0
        ? pkg.items.map((item) => {
            const productName = item.name || item.product?.BPPRODUCT_NAME || 'Product';
            const { quantity, finalPrice, priceType } = getPriceForItem(item);
            const priceLabel = priceType === 'corporate' ? ' [Corp]' : priceType === 'agent' ? ' [Eco]' : '';
            return `- ${productName} (Qty: ${quantity})${priceLabel} - Rp ${(finalPrice * quantity).toLocaleString('id-ID')}`;
          }).join('\n')
        : 'No items';

      const subject = encodeURIComponent(`Package: ${pkg.package_name}`);
      const body = encodeURIComponent(
        `Package Details:\n\n` +
        `Name: ${pkg.package_name}\n` +
        `${pkg.description ? `Description: ${pkg.description}\n` : ''}` +
        `${pkg.valid_date ? `Start Date: ${new Date(pkg.valid_date).toLocaleDateString('id-ID')}\n` : ''}` +
        `${pkg.valid_end_date ? `End Date: ${new Date(pkg.valid_end_date).toLocaleDateString('id-ID')}\n` : ''}` +
        `${pkg.quota ? `Quota: ${pkg.quota}\n` : ''}` +
        `\nItems:\n${itemsText}` +
        `\n\nTotal Price: Rp ${totalFromItems.toLocaleString('id-ID')}`
      );

      window.location.href = `mailto:?subject=${subject}&body=${body}`;
    } catch (error) {
      console.error('Error opening email:', error);
      toast({
        title: 'Error',
        description: 'Gagal membuka email client',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    }
  };

  // Load inventory items from API
  const loadInventoryItems = useCallback(async () => {
    try {
      const params = {};
      if (searchInventoryQuery) {
        params.search = searchInventoryQuery;
      }
      
      const response = await bpProductNewApi.get('/browse', { params });
      
      if (response.data.status === 200) {
        let products = response.data.data || [];
        
        // Map products to inventory format
        const mappedProducts = products.map(product => {
          // For retail products, use different location logic
          const location = product.product_type === 'retail' 
            ? 'Injourney Retail'
            : (product.COMPANY_NAME || MEMBER_CODE_MAPPING[product.MEMBER_CODE?.toLowerCase()] || product.MEMBER_CODE || '-');
          
          return {
            id: product.BPPRODUCT_ID,
            name: product.BPPRODUCT_NAME || '-',
            description: product.BPPRODUCT_DESC || '-',
            location: location,
            price: parseFloat(product.BPPRODUCT_PUBLISHPRICE) || 0,
            originalPrice: parseFloat(product.BPPRODUCT_PUBLISHPRICE) || 0,
            productCode: product.BPPRODUCT_CODE,
            productType: product.product_type || 'product',
            quantity: product.BPPRODUCT_QUANTITY || 0,
            memberCode: product.MEMBER_CODE,
            categoryName: product.BPPRODUCT_CATEGORY,
            corporatePrice: parseFloat(product.BPPRODUCT_CORPPRICE) || null,
            agentPrice: parseFloat(product.BPPRODUCT_AGENTPRICE) || null,
            unit: product.BPPRODUCT_UNIT,
            variantKey: product.BPPRODUCT_VARIANT_KEY,
            variantName: product.BPPRODUCT_VARIANT_NAME,
          };
        });
        
        setInventoryItems(mappedProducts);
      }
    } catch (error) {
      console.error('Error fetching inventory items:', error);
      toast({
        title: 'Error',
        description: error.response?.data?.message || 'Terjadi kesalahan saat mengambil data inventory',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      setInventoryItems([]);
    }
  }, [searchInventoryQuery, toast]);

  // Handle open add item modal
  const handleOpenAddItem = (pkg) => {
    setEditingPackage(pkg);
    setSearchInventoryQuery('');
    setSelectedInventoryItem(null);
    setAddItemQuantity(1);
    setAddItemPrice(0);
    onAddItemOpen();
  };

  useEffect(() => {
    if (isAddItemOpen) {
      loadInventoryItems();
    }
  }, [isAddItemOpen, loadInventoryItems, searchInventoryQuery]);

  // Handle select inventory item
  const handleSelectInventoryItem = (item) => {
    setSelectedInventoryItem(item);
    setAddItemQuantity(1);
    // For non-retail, use publish price (cannot be changed)
    // For retail, use original price (can be changed)
    const originalPrice = item.price || item.originalPrice || 0;
    setAddItemPrice(originalPrice);
    setAddItemPriceType('bundle');
    setAddItemDiscountPercent(0);
    setAddItemSelectedPriceType('base'); // Reset to base price
  };

  // Handle price type selection for non-retail products
  const handleSelectPriceType = (priceType, item) => {
    setAddItemSelectedPriceType(priceType);
    let selectedPrice = 0;
    
    if (priceType === 'base') {
      selectedPrice = item.price || item.originalPrice || 0;
    } else if (priceType === 'corporate') {
      selectedPrice = item.corporatePrice || item.price || 0;
    } else if (priceType === 'agent') {
      selectedPrice = item.agentPrice || item.price || 0;
    }
    
    setAddItemPrice(selectedPrice);
  };

  // Handle add item to package
  const handleAddItemToPackage = async () => {
    if (!editingPackage || !selectedInventoryItem) {
      toast({
        title: 'Validasi gagal',
        description: 'Pilih item terlebih dahulu',
        status: 'warning',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    try {
      const packageId = editingPackage.PACKAGE_ID || editingPackage.id;
      
      // Determine which product ID to use based on product type
      const productId = selectedInventoryItem.id;
      
      const requestData = {
        PACKAGEDETAIL_STATUS: '1',
        PACKAGEDETAIL_QUANTITY: addItemQuantity || 1,
        PACKAGEDETAIL_PRICETYPE: selectedInventoryItem.productType === 'retail' ? 'base' : addItemSelectedPriceType
      };
      
      if (selectedInventoryItem.productType === 'retail') {
        requestData.BPPRODUCTRETAIL_ID = productId;
        requestData.BPPRODUCT_ID = null;
      } else {
        requestData.BPPRODUCT_ID = productId;
        requestData.BPPRODUCTRETAIL_ID = null;
      }

      const response = await packageApi.post(`/${packageId}/add-item`, requestData);

      if (response.data.status === 201) {
        toast({
          title: 'Berhasil',
          description: `Item berhasil ditambahkan ke ${editingPackage.package_name}`,
          status: 'success',
          duration: 3000,
          isClosable: true,
        });

        onAddItemClose();
        setSelectedInventoryItem(null);
        await fetchPackages();
      }
    } catch (error) {
      console.error('Error adding item to package:', error);
      toast({
        title: 'Error',
        description: error.response?.data?.message || 'Terjadi kesalahan saat menambahkan item ke package',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    }
  };

  // Filter inventory items by search (already filtered by API, but can add client-side filter if needed)
  const filteredInventoryItems = inventoryItems.filter(item => {
    if (!searchInventoryQuery) return true;
    const query = searchInventoryQuery.toLowerCase();
    return (
      item.name?.toLowerCase().includes(query) ||
      item.description?.toLowerCase().includes(query) ||
      item.location?.toLowerCase().includes(query) ||
      item.productCode?.toLowerCase().includes(query)
    );
  });

  const filteredPackages = packages.filter((pkg) => {
    const q = listSearch.trim().toLowerCase();
    const visual = getVisualStatus(pkg).toLowerCase();
    const readiness = getPackageBlockers(pkg).length === 0 ? 'ready' : (pkg.items?.length ? 'incomplete' : 'blocked');
    const matchesSearch = !q || `${pkg.package_name} ${pkg.PACKAGE_CODE || ''} ${pkg.pic_member || ''}`.toLowerCase().includes(q);
    const matchesPublication = publicationFilter === 'all' || visual === publicationFilter;
    const matchesReadiness = readinessFilter === 'all' || readiness === readinessFilter;
    return matchesSearch && matchesPublication && matchesReadiness;
  });
  const listPageSize = 25;
  const listPageCount = Math.max(1, Math.ceil(filteredPackages.length / listPageSize));
  const visiblePackages = filteredPackages.slice((listPage - 1) * listPageSize, listPage * listPageSize);
  const togglePackage = (id) => setSelectedPackages((current) => current.includes(id) ? current.filter((item) => item !== id) : [...current, id]);

  return (
    <Box>
      <VStack align="stretch" spacing={5} mb={6}>
        <HStack justify="space-between" align={{ base: 'stretch', md: 'end' }} flexDirection={{ base: 'column', md: 'row' }} spacing={4}>
          <VStack align="start" spacing={1} maxW="680px">
            <Text fontSize="xs" fontWeight="700" color="accent.blue" textTransform="uppercase" letterSpacing="0.08em">
              Product Manager
            </Text>
            <Heading size={{ base: 'md', lg: 'lg' }} color="label.primary" letterSpacing="-0.02em">
              My Products
            </Heading>
            <Text fontSize="sm" color="label.secondary" lineHeight="1.7">
            Compose, validate, dan maintain package lintas member dalam satu workspace.
            </Text>
          </VStack>
          <HStack spacing={3} flexWrap="wrap">
            <Button
              leftIcon={<FiRefreshCw />}
              onClick={fetchPackages}
              isLoading={loading}
              variant="outline"
              colorScheme="brand"
              size={{ base: 'sm', lg: 'md' }}
            >
              Refresh
            </Button>
            <Button
              leftIcon={<FiPlus />}
              onClick={handleCreate}
              colorScheme="brand"
              size={{ base: 'sm', lg: 'md' }}
            >
              <Text display={{ base: 'none', sm: 'block' }}>Tambah Package</Text>
              <Text display={{ base: 'block', sm: 'none' }}>Tambah</Text>
            </Button>
          </HStack>
        </HStack>

        <SummaryCardGroup
          items={[
            { label: 'Total Package', value: packageDashboard.total, helper: `${packageDashboard.items} total item` },
            { label: 'Published', value: packageDashboard.Published, color: '#047857', helper: 'Live di API Hub' },
            { label: 'Ready', value: packageDashboard.Ready, color: '#0369A1', helper: 'Siap dipreview/publish' },
            { label: 'Blockers', value: packageDashboard.blockers, color: packageDashboard.blockers > 0 ? '#B45309' : '#047857', helper: 'Readiness issue terbuka' },
          ]}
        />
        <Box bg="white" border="1px solid" borderColor="gray.200" borderRadius="lg" overflow="hidden">
          <Box p={4} borderBottom="1px solid" borderColor="gray.200">
            <HStack spacing={3} align="end" flexWrap="wrap">
              <FormControl maxW={{ base: 'full', md: '360px' }}>
                <FormLabel fontSize="xs" mb={1}>Search</FormLabel>
                <InputGroup><InputLeftElement pointerEvents="none"><FiSearch color="#94A3B8" /></InputLeftElement><Input value={listSearch} onChange={(e) => { setListSearch(e.target.value); setListPage(1); }} placeholder="Cari package, code, atau member..." /></InputGroup>
              </FormControl>
              <FormControl maxW="200px"><FormLabel fontSize="xs" mb={1}>Publication</FormLabel><Select value={publicationFilter} onChange={(e) => { setPublicationFilter(e.target.value); setListPage(1); }}><option value="all">Semua Status</option><option value="published">Published</option><option value="ready">Ready to Publish</option><option value="draft">Draft</option><option value="archived">Archived</option></Select></FormControl>
              <FormControl maxW="180px"><FormLabel fontSize="xs" mb={1}>Readiness</FormLabel><Select value={readinessFilter} onChange={(e) => { setReadinessFilter(e.target.value); setListPage(1); }}><option value="all">Semua</option><option value="ready">Ready</option><option value="incomplete">Incomplete</option><option value="blocked">Blocked</option></Select></FormControl>
            </HStack>
          </Box>
          {selectedPackages.length > 0 && <HStack px={4} py={3} bg="blue.50" borderBottom="1px solid" borderColor="blue.100" spacing={3}><Text fontSize="sm" fontWeight="700" color="blue.800">{selectedPackages.length} item dipilih</Text><Button size="xs" variant="outline" onClick={() => setSelectedPackages([])}>Clear</Button><Text fontSize="xs" color="blue.700">Publish, unpublish, dan delete tetap divalidasi per package melalui action masing-masing.</Text></HStack>}
          <Box overflowX="auto">
            <Box as="table" w="full" minW="1050px" fontSize="sm">
              <Box as="thead" bg="gray.50"><Box as="tr"><Box as="th" p={3} textAlign="left"><Checkbox isChecked={visiblePackages.length > 0 && visiblePackages.every((p) => selectedPackages.includes(p.id))} onChange={(e) => setSelectedPackages(e.target.checked ? [...new Set([...selectedPackages, ...visiblePackages.map((p) => p.id)])] : selectedPackages.filter((id) => !visiblePackages.some((p) => p.id === id)))} /></Box>{['Package','Code','Items','Member','Active Period','Total Price','Readiness','Publication','Updated','Action'].map((h) => <Box as="th" key={h} p={3} textAlign="left" color="gray.600" whiteSpace="nowrap">{h}</Box>)}</Box></Box>
              <Box as="tbody">{loading ? Array.from({ length: 5 }, (_, i) => <Box as="tr" key={i} borderTop="1px solid" borderColor="gray.100"><Box as="td" colSpan={11} p={3}><Skeleton h="28px" /></Box></Box>) : visiblePackages.length === 0 ? <Box as="tr"><Box as="td" colSpan={11} p={10} textAlign="center"><Text fontWeight="700">No packages found</Text><Text fontSize="sm" color="gray.500">Try adjusting your search or filters.</Text></Box></Box> : visiblePackages.map((pkg) => { const blockers = getPackageBlockers(pkg); const readiness = blockers.length === 0 ? 'Ready' : (pkg.items?.length ? 'Incomplete' : 'Blocked'); const summary = getPackageSummary(pkg); return <Box as="tr" key={pkg.id} borderTop="1px solid" borderColor="gray.100" _hover={{ bg: 'gray.50' }}><Box as="td" p={3}><Checkbox isChecked={selectedPackages.includes(pkg.id)} onChange={() => togglePackage(pkg.id)} /></Box><Box as="td" p={3} minW="230px"><Button variant="link" colorScheme="blue" fontSize="sm" onClick={() => handleWizardResume(pkg)}>{pkg.package_name}</Button><Text noOfLines={1} fontSize="xs" color="gray.500">{pkg.description || '—'}</Text></Box><Box as="td" p={3} fontFamily="mono" fontSize="xs">{pkg.PACKAGE_CODE || '—'}</Box><Box as="td" p={3}>{pkg.items?.length || 0}</Box><Box as="td" p={3}>{MEMBER_CODE_MAPPING[pkg.pic_member?.toLowerCase()] || pkg.pic_member || '—'}</Box><Box as="td" p={3} whiteSpace="nowrap">{pkg.valid_date ? new Date(pkg.valid_date).toLocaleDateString('id-ID') : '—'} – {pkg.valid_end_date ? new Date(pkg.valid_end_date).toLocaleDateString('id-ID') : '—'}</Box><Box as="td" p={3} whiteSpace="nowrap">Rp {(summary.totalFinalPrice || pkg.total_price || 0).toLocaleString('id-ID')}</Box><Box as="td" p={3}><Badge colorScheme={readiness === 'Ready' ? 'green' : readiness === 'Blocked' ? 'red' : 'orange'}>{readiness}</Badge></Box><Box as="td" p={3}><StatusChip status={getVisualStatus(pkg)} /></Box><Box as="td" p={3} whiteSpace="nowrap">{pkg.created_at ? new Date(pkg.created_at).toLocaleDateString('id-ID') : '—'}</Box><Box as="td" p={3}><Box as="details" position="relative"><Box as="summary" cursor="pointer" listStyleType="none" aria-label={`Actions for ${pkg.package_name}`}>•••</Box><VStack align="stretch" position="absolute" right="0" zIndex="20" minW="170px" bg="white" border="1px solid" borderColor="gray.200" borderRadius="md" boxShadow="lg" p={1}><Button size="xs" variant="ghost" justifyContent="start" onClick={() => handleWizardResume(pkg)}>View / Open Wizard</Button><Button size="xs" variant="ghost" justifyContent="start" onClick={() => handleEdit(pkg)}>Edit Package</Button><Button size="xs" variant="ghost" justifyContent="start" onClick={() => handleOpenAddItem(pkg)}>Manage Items</Button><Button size="xs" variant="ghost" justifyContent="start" onClick={() => handleManageImages(pkg)}>Images</Button><Button size="xs" variant="ghost" justifyContent="start" onClick={() => handleShare(pkg)}>Share</Button></VStack></Box></Box></Box>; })}</Box>
            </Box>
          </Box>
          <HStack justify="space-between" p={4} borderTop="1px solid" borderColor="gray.200"><Text fontSize="sm" color="gray.500">Showing {filteredPackages.length ? (listPage - 1) * listPageSize + 1 : 0}–{Math.min(listPage * listPageSize, filteredPackages.length)} of {filteredPackages.length} packages</Text><HStack><Button size="sm" variant="outline" isDisabled={listPage <= 1} onClick={() => setListPage((p) => p - 1)}>Previous</Button><Text fontSize="sm">{listPage} / {listPageCount}</Text><Button size="sm" variant="outline" isDisabled={listPage >= listPageCount} onClick={() => setListPage((p) => p + 1)}>Next</Button></HStack></HStack>
        </Box>
      </VStack>

      <Grid 
        display="none"
        templateColumns={{ base: 'repeat(1, 1fr)', md: 'repeat(2, 1fr)', lg: 'repeat(3, 1fr)' }} 
        gap={{ base: 4, lg: 6 }}
      >
        {loading ? (
          Array.from({ length: 3 }).map((_, index) => (
            <GridItem key={`package-skeleton-${index}`}>
              <Card bg="surface.tertiary" borderRadius="lg" boxShadow="primeSm" h="100%">
                <CardBody>
                  <VStack align="stretch" spacing={4}>
                    <Skeleton height="24px" borderRadius="md" />
                    <SkeletonText noOfLines={2} spacing={2} skeletonHeight="10px" />
                    <Skeleton height="72px" borderRadius="md" />
                    <Skeleton height="120px" borderRadius="md" />
                  </VStack>
                </CardBody>
              </Card>
            </GridItem>
          ))
        ) : packages.length === 0 ? (
          <GridItem colSpan={{ base: 1, md: 2, lg: 3 }}>
            <EmptyStateBlock
              icon={FiPackage}
              title="Belum ada package"
              description="Mulai buat bundling package pertama agar produk lintas member punya draft, readiness check, dan ringkasan harga yang jelas."
              actionLabel="Tambah Package"
              onAction={handleCreate}
              secondaryText="Package yang sudah dibuat akan muncul dengan status lifecycle dan blocker readiness."
            />
          </GridItem>
        ) : (
          packages.map((pkg) => (
            <GridItem key={pkg.id}>
              <Card
                bg="surface.tertiary"
                borderRadius="lg"
                boxShadow="primeSm"
                border="0.5px solid"
                borderColor="rgba(15, 23, 42, 0.10)"
                h="100%"
              >
                <CardBody>
                  <VStack align="stretch" spacing={4}>
                    <VStack align="start" spacing={1}>
                      <HStack justify="space-between" w="full" align="start">
                        <Heading size="md" color="gray.800" fontWeight="700" pr={2}>
                          {pkg.package_name}
                        </Heading>
                        <HStack spacing={1.5}>
                          <StatusChip status={getVisualStatus(pkg)} />
                        </HStack>
                      </HStack>
                      <HStack spacing={2} flexWrap="wrap">
                        {pkg.PACKAGE_CODE && (
                          <Badge colorScheme="blue" fontSize="xs" px={2} py={0.5} borderRadius="md">
                            {pkg.PACKAGE_CODE}
                          </Badge>
                        )}
                        {pkg.items?.length > 0 && (
                          <Badge colorScheme="gray" fontSize="xs" px={2} py={0.5} borderRadius="md">
                            {pkg.items.length} item
                          </Badge>
                        )}
                        {pkg.pic_member && (
                          <DomainBadge
                            domain={pkg.pic_member}
                            label={MEMBER_CODE_MAPPING[pkg.pic_member?.toLowerCase()] || pkg.pic_member}
                          />
                        )}
                      </HStack>
                    </VStack>
                    
                    {pkg.description && (
                      <MarkdownText>{pkg.description}</MarkdownText>
                    )}

                    {(pkg.valid_date || pkg.valid_end_date || pkg.quota || pkg.pic_member) && (
                      <VStack align="stretch" spacing={2} pt={2} borderTop="1px solid" borderColor="gray.100">
                        {pkg.valid_date && (
                          <HStack justify="space-between">
                            <Text fontSize="xs" color="gray.500">
                              Start Date
                            </Text>
                            <Text fontSize="xs" fontWeight="600" color="gray.700">
                              {new Date(pkg.valid_date).toLocaleDateString('id-ID')}
                            </Text>
                          </HStack>
                        )}
                        {pkg.valid_end_date && (
                          <HStack justify="space-between">
                            <Text fontSize="xs" color="gray.500">
                              End Date
                            </Text>
                            <Text fontSize="xs" fontWeight="600" color="gray.700">
                              {new Date(pkg.valid_end_date).toLocaleDateString('id-ID')}
                            </Text>
                          </HStack>
                        )}
                        {pkg.quota && (
                          <HStack justify="space-between">
                            <Text fontSize="xs" color="gray.500">
                              Kuota
                            </Text>
                            <Text fontSize="xs" fontWeight="600" color="gray.700">
                              {parseInt(pkg.quota).toLocaleString('id-ID')}
                            </Text>
                          </HStack>
                        )}
                        {pkg.pic_member && (
                          <HStack justify="space-between">
                            <Text fontSize="xs" color="gray.500">
                              PIC Member
                            </Text>
                            <DomainBadge domain={pkg.pic_member} label={MEMBER_CODE_MAPPING[pkg.pic_member?.toLowerCase()] || pkg.pic_member} />
                          </HStack>
                        )}
                      </VStack>
                    )}
                    
                    <ReadinessPanel blockers={getPackageBlockers(pkg)} />

                    {/* Banner CTA — muncul saat paket Ready dan belum dipublish */}
                    {getVisualStatus(pkg) === 'Ready' && !pkg._isIndividualProduct && (
                      <Box bg="green.50" border="1px solid" borderColor="green.200" borderRadius="md" px={3} py={2.5}>
                        <HStack justify="space-between" align="center">
                          <HStack spacing={2}>
                            <Icon as={FiCheckCircle} color="green.600" boxSize={4} />
                            <Text fontSize="xs" fontWeight="600" color="green.700">
                              Paket siap dipublish ke API Hub
                            </Text>
                          </HStack>
                          <Button
                            size="xs"
                            colorScheme="green"
                            leftIcon={<FiSend />}
                            onClick={async () => {
                              try {
                                const res = await packageApi.post(`/publish/${pkg.PACKAGE_ID}`, {});
                                if (res.data?.status === 200) {
                                  setPackages(prev => prev.map(p =>
                                    (p.PACKAGE_ID || p.id) === pkg.PACKAGE_ID
                                      ? { ...p, PACKAGE_APISTATUS: 'published' }
                                      : p
                                  ));
                                  toast({ title: 'Published!', description: res.data.message || `"${pkg.package_name}" tersedia di API Hub.`, status: 'success', duration: 4000, isClosable: true });
                                }
                              } catch (err) {
                                toast({ title: 'Gagal Publish', description: err.response?.data?.message || err.message, status: 'error', duration: 5000, isClosable: true });
                              }
                            }}
                          >
                            Publish Sekarang
                          </Button>
                        </HStack>
                      </Box>
                    )}

                    <Divider />

                    {/* Publish/Unpublish action — dipindah ke bawah, Fix 3: tampil selama tidak Archived */}
                    {!pkg._isIndividualProduct && getVisualStatus(pkg) !== 'Archived' && (
                      <Box>
                        {pkg.PACKAGE_APISTATUS === 'published' ? (
                          <Button
                            leftIcon={<FiSend />}
                            size="xs"
                            colorScheme="orange"
                            variant="outline"
                            onClick={async () => {
                              try {
                                const res = await packageApi.post(`/unpublish/${pkg.PACKAGE_ID}`);
                                if (res.data?.status === 200) {
                                  setPackages(prev => prev.map(p =>
                                    (p.PACKAGE_ID || p.id) === pkg.PACKAGE_ID
                                      ? { ...p, PACKAGE_APISTATUS: 'draft' }
                                      : p
                                  ));
                                  toast({ title: 'Unpublished', description: `"${pkg.package_name}" ditarik dari API Hub.`, status: 'info', duration: 3000, isClosable: true });
                                }
                              } catch (err) {
                                toast({ title: 'Gagal', description: err.response?.data?.message || err.message, status: 'error', duration: 4000, isClosable: true });
                              }
                            }}
                          >
                            Unpublish from API
                          </Button>
                        ) : (
                          <Button
                            leftIcon={<FiSend />}
                            size="xs"
                            colorScheme="emerald"
                            onClick={async () => {
                              try {
                                const res = await packageApi.post(`/publish/${pkg.PACKAGE_ID}`, {});
                                if (res.data?.status === 200) {
                                  setPackages(prev => prev.map(p =>
                                    (p.PACKAGE_ID || p.id) === pkg.PACKAGE_ID
                                      ? { ...p, PACKAGE_APISTATUS: 'published' }
                                      : p
                                  ));
                                  toast({ title: 'Published!', description: res.data.message || `"${pkg.package_name}" tersedia di API Hub.`, status: 'success', duration: 4000, isClosable: true });
                                }
                              } catch (err) {
                                toast({ title: 'Gagal Publish', description: err.response?.data?.message || err.message, status: 'error', duration: 5000, isClosable: true });
                              }
                            }}
                          >
                            Publish to API
                          </Button>
                        )}
                      </Box>
                    )}

                    {/* Items List */}
                    <VStack align="stretch" spacing={2} mt={2}>
                      <HStack justify="space-between" pb={2}>
                        <Text fontSize="sm" fontWeight="600" color="gray.700">
                          Items ({pkg.items?.length || 0})
                        </Text>
                        <Button
                          leftIcon={<FiPlus />}
                          size="sm"
                          colorScheme="brand"
                          variant="outline"
                          onClick={() => handleOpenAddItem(pkg)}
                        >
                          Add Item
                        </Button>
                      </HStack>
                      {pkg.items && pkg.items.length > 0 && (
                        <VStack align="stretch" spacing={2} maxH="300px" overflowY="auto">
                          {pkg.items.map((item, index) => {
                            // Handle both old format (with name, quantity, etc.) and new format (with product object)
                            const productName = item.name || item.product?.BPPRODUCT_NAME || item.product?.name || 'Product';
                            const productCode = item.product?.BPPRODUCT_CODE || item.product?.productCode || '-';
                            const { quantity, priceType, basePrice, finalPrice, subtotal } = getPriceForItem(item);
                            
                            return (
                              <Card
                                key={item.PACKAGEDETAIL_ID || item.id || `item-${index}`}
                                bg="surface.tertiary"
                                border="0.5px solid"
                                borderColor="rgba(15, 23, 42, 0.12)"
                                p={3}
                                size="sm"
                                borderRadius="md"
                                boxShadow="none"
                              >
                                <VStack align="stretch" spacing={2}>
                                  <HStack justify="space-between">
                                    <VStack align="start" spacing={0} flex={1}>
                                      <Text fontSize="sm" fontWeight="600" noOfLines={1} color="gray.800">
                                        {productName}
                                      </Text>
                                      <HStack spacing={2} fontSize="xs" color="gray.500" mt={1}>
                                        <Text>Code: {productCode}</Text>
                                        {quantity > 1 && <Text>• Qty: {quantity}</Text>}
                                      </HStack>
                                    </VStack>
                                    <IconButton
                                      icon={<FiTrash2 />}
                                      size="xs"
                                      colorScheme="red"
                                      variant="ghost"
                                      onClick={() => {
                                        setEditingPackage(pkg);
                                        setDeleteItem(item);
                                        onDeleteItemOpen();
                                      }}
                                      aria-label="Delete Item"
                                    />
                                  </HStack>
                                  
                                  {/* Pricing Information */}
                                  {basePrice > 0 && (
                                    <VStack align="stretch" spacing={1} pt={2} borderTop="1px solid" borderColor="gray.200">
                                      <HStack justify="space-between" fontSize="xs">
                                        <Text color="gray.600">Price Type:</Text>
                                        <Badge colorScheme={priceType === 'base' ? 'blue' : priceType === 'corporate' ? 'green' : 'purple'}>
                                          {priceType === 'base' ? 'Publish Price' : priceType === 'corporate' ? 'Corporate Price' : 'Ecosystem Price'}
                                        </Badge>
                                      </HStack>
                                      <HStack justify="space-between" fontSize="xs">
                                        <Text color="gray.600">Price:</Text>
                                        <Text fontWeight="600" color="gray.700">
                                          Rp {finalPrice.toLocaleString('id-ID')}
                                        </Text>
                                      </HStack>
                                      {finalPrice !== basePrice && (
                                        <HStack justify="space-between" fontSize="xs">
                                          <Text color="gray.500" fontSize="2xs">Base Price:</Text>
                                          <Text color="gray.500" fontSize="2xs" textDecoration="line-through">
                                            Rp {basePrice.toLocaleString('id-ID')}
                                          </Text>
                                        </HStack>
                                      )}
                                      <HStack justify="space-between" fontSize="xs" pt={1} borderTop="1px solid" borderColor="gray.200">
                                        <Text color="gray.700" fontWeight="600">Subtotal:</Text>
                                        <Text fontWeight="700" color="brand.600">
                                          Rp {subtotal.toLocaleString('id-ID')}
                                        </Text>
                                      </HStack>
                                    </VStack>
                                  )}
                                </VStack>
                              </Card>
                            );
                          })}
                        </VStack>
                      )}
                    </VStack>

                    <Divider />

                    {pkg.items && pkg.items.length > 0 && (
                      <Box
                        pt={2}
                        borderTop="1px solid"
                        borderColor="gray.200"
                        pb={1}
                      >
                        {(() => {
                          const summary = getPackageSummary(pkg);
                          return (
                            <PriceSummaryBlock
                              totalBasePrice={summary.totalBasePrice}
                              totalFinalPrice={summary.totalFinalPrice}
                            />
                          );
                        })()}
                      </Box>
                    )}

                    {/* Footer: Action Buttons */}
                    <Divider />
                    {pkg._isIndividualProduct ? (
                      <SimpleGrid columns={2} spacing={2} pt={2}>
                        {pkg.PACKAGE_APISTATUS === 'published' ? (
                          <Button
                            leftIcon={<FiSend />}
                            size="sm"
                            colorScheme="orange"
                            variant="outline"
                            onClick={async () => {
                              try {
                                const prodId = String(pkg.id).replace('prod-', '');
                                const res = await bpProductNewApi.post(`/unpublish/${prodId}`);
                                if (res.data?.status === 200) {
                                  setPackages(prev => prev.map(p =>
                                    p.id === pkg.id ? { ...p, PACKAGE_APISTATUS: 'draft' } : p
                                  ));
                                  toast({ title: 'Unpublished', description: `"${pkg.package_name}" ditarik dari API Hub.`, status: 'info', duration: 3000, isClosable: true });
                                }
                              } catch (err) {
                                toast({ title: 'Gagal', description: err.response?.data?.message || err.message, status: 'error', duration: 4000, isClosable: true });
                              }
                            }}
                          >
                            Unpublish from API
                          </Button>
                        ) : (
                          <Button
                            leftIcon={<FiSend />}
                            size="sm"
                            colorScheme="emerald"
                            variant="outline"
                            onClick={async () => {
                              try {
                                const prodId = String(pkg.id).replace('prod-', '');
                                const res = await bpProductNewApi.post(`/publish/${prodId}`);
                                if (res.data?.status === 200) {
                                  setPackages(prev => prev.map(p =>
                                    p.id === pkg.id ? { ...p, PACKAGE_APISTATUS: 'published' } : p
                                  ));
                                  toast({ title: 'Published!', description: `"${pkg.package_name}" tersedia di API Hub.`, status: 'success', duration: 4000, isClosable: true });
                                }
                              } catch (err) {
                                toast({ title: 'Gagal Publish', description: err.response?.data?.message || err.message, status: 'error', duration: 5000, isClosable: true });
                              }
                            }}
                          >
                            Publish to API
                          </Button>
                        )}
                        <Button
                          leftIcon={<FiTrash2 />}
                          size="sm"
                          colorScheme="red"
                          variant="outline"
                          onClick={async () => {
                            if (!window.confirm(`Hapus "${pkg.package_name}"?`)) return;
                            try {
                              const prodId = String(pkg.id).replace('prod-', '');
                              await bpProductNewApi.post(`/delete/${prodId}`);
                              toast({ title: 'Terhapus', status: 'info', duration: 3000, isClosable: true });
                              fetchPackages();
                            } catch (err) {
                              toast({ title: 'Gagal', description: err.message, status: 'error', duration: 4000, isClosable: true });
                            }
                          }}
                        >
                          Delete
                        </Button>
                      </SimpleGrid>
                    ) : (
                      <SimpleGrid columns={{ base: 2, md: 4 }} spacing={2} pt={2}>
                        <Button
                          leftIcon={<FiImage />}
                          size="sm"
                          colorScheme="blue"
                          variant="outline"
                          onClick={() => handleManageImages(pkg)}
                        >
                          Images
                        </Button>
                        <Button
                          leftIcon={<FiEdit />}
                          size="sm"
                          colorScheme="blue"
                          variant="outline"
                          onClick={() => handleWizardResume(pkg)}
                        >
                          Wizard
                        </Button>
                        <Button
                          leftIcon={<FiShare2 />}
                          size="sm"
                          colorScheme="purple"
                          variant="outline"
                          onClick={() => handleShare(pkg)}
                        >
                          Share
                        </Button>
                        <Button
                          leftIcon={<FiCopy />}
                          size="sm"
                          colorScheme="teal"
                          variant="outline"
                          onClick={() => handleCopyToClipboard(pkg)}
                        >
                          Copy
                        </Button>
                        <Button
                          leftIcon={<FiMail />}
                          size="sm"
                          colorScheme="orange"
                          variant="outline"
                          onClick={() => handleEmail(pkg)}
                        >
                          Email
                        </Button>
                        <Button
                          leftIcon={<FiTrash2 />}
                          size="sm"
                          colorScheme="red"
                          variant="outline"
                          onClick={() => handleDelete(pkg)}
                        >
                          Delete
                        </Button>
                      </SimpleGrid>
                    )}
                  </VStack>
                </CardBody>
              </Card>
            </GridItem>
          ))
        )}
      </Grid>

      {/* Create/Edit Modal */}
      <Modal isOpen={isFormOpen} onClose={onFormClose} size="xl">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>
            {editingPackage ? 'Edit Package' : 'Tambah Package Baru'}
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack spacing={4}>
              <FormControl isRequired>
                <FormLabel>Package Name</FormLabel>
                <Input
                  name="package_name"
                  value={formData.package_name}
                  onChange={handleInputChange}
                  placeholder="Masukkan package name"
                />
              </FormControl>
              
              {!editingPackage && (
                <FormControl>
                  <FormLabel>Package Code</FormLabel>
                  <Input
                    value={formData.package_code || ''}
                    isReadOnly
                    bg="gray.50"
                    placeholder="Akan di-generate otomatis"
                  />
                  <Text fontSize="xs" color="gray.500" mt={1}>
                    Package code akan di-generate otomatis saat menyimpan
                  </Text>
                </FormControl>
              )}
              
              {editingPackage && editingPackage.PACKAGE_CODE && (
                <FormControl>
                  <FormLabel>Package Code</FormLabel>
                  <Input
                    value={editingPackage.PACKAGE_CODE}
                    isReadOnly
                    bg="gray.50"
                  />
                </FormControl>
              )}

              <FormControl>
                <FormLabel>Description</FormLabel>
                <Textarea
                  name="description"
                  value={formData.description}
                  onChange={handleInputChange}
                  placeholder={'Masukkan description.\n\n**Promo spesial**\n- Poin pertama\n- Poin kedua'}
                  rows={4}
                />
                <Text fontSize="xs" color="gray.500" mt={1}>{MD_HINT}</Text>
                {formData.description?.trim() && (
                  <Box mt={2} p={3} borderWidth="1px" borderRadius="md" bg="gray.50">
                    <Text fontSize="xs" color="gray.400" mb={1}>Preview</Text>
                    <MarkdownText>{formData.description}</MarkdownText>
                  </Box>
                )}
              </FormControl>

              <FormControl>
                <FormLabel>Price Includes</FormLabel>
                <Textarea
                  name="price_includes"
                  value={formData.price_includes}
                  onChange={handleInputChange}
                  placeholder="Contoh: Tiket masuk, welcome drink"
                  rows={3}
                />
              </FormControl>

              <FormControl>
                <FormLabel>Price Excludes</FormLabel>
                <Textarea
                  name="price_excludes"
                  value={formData.price_excludes}
                  onChange={handleInputChange}
                  placeholder="Contoh: Transportasi, oleh-oleh"
                  rows={3}
                />
              </FormControl>

              <SimpleGrid columns={2} spacing={4}>
                <FormControl>
                  <FormLabel>Start Date</FormLabel>
                  <Input
                    name="valid_date"
                    type="date"
                    value={formData.valid_date}
                    onChange={handleInputChange}
                  />
                </FormControl>

                <FormControl>
                  <FormLabel>End Date</FormLabel>
                  <Input
                    name="valid_end_date"
                    type="date"
                    value={formData.valid_end_date}
                    onChange={handleInputChange}
                    min={formData.valid_date || ''}
                  />
                </FormControl>
              </SimpleGrid>

              <FormControl>
                <FormLabel>Kuota</FormLabel>
                <NumberInput
                  value={formData.quota}
                  onChange={(valueString) => setFormData({ ...formData, quota: valueString })}
                  min={0}
                >
                  <NumberInputField placeholder="Masukkan kuota" />
                  <NumberInputStepper>
                    <NumberIncrementStepper />
                    <NumberDecrementStepper />
                  </NumberInputStepper>
                </NumberInput>
              </FormControl>

              <FormControl>
                <FormLabel>PIC Member</FormLabel>
                <Select
                  name="pic_member"
                  value={formData.pic_member}
                  onChange={handleInputChange}
                  placeholder="Pilih PIC Member"
                >
                  {Object.entries(MEMBER_CODE_MAPPING).map(([code, name]) => (
                    <option key={code} value={code}>
                      {name}
                    </option>
                  ))}
                </Select>
              </FormControl>
            </VStack>
          </ModalBody>

          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={onFormClose}>
              Batal
            </Button>
            <Button colorScheme="brand" onClick={handleSubmit}>
              {editingPackage ? 'Update' : 'Simpan'}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      <Modal
        isOpen={isWizardOpen}
        onClose={handleWizardRequestClose}
        size="6xl"
        closeOnOverlayClick={false}
      >
        <ModalOverlay />
        <ModalContent maxH="92vh" overflowY="auto">
          <ModalBody p={{ base: 4, md: 6 }}>
            <WizardShell
              title={wizardForm.package_name ? wizardForm.package_name : 'Create Bundling Package'}
              subtitle="Step 1 menyiapkan metadata package, Step 2 memilih produk, Step 3 konfigurasi komersial, dan Step 4 final review sebelum publish."
              steps={WIZARD_STEPS}
              activeStep={wizardStep}
              canContinue={
                wizardStep === 0
                  ? Boolean(wizardForm.package_name?.trim())
                  : wizardStep === 1
                    ? Boolean(wizardSelectedItems.length > 0 || wizardSelectedInventoryItem)
                    : wizardStep === 2
                      ? true
                      : getWizardPublishBlockers().length === 0
              }
              isSaving={wizardSaving}
              onBack={handleWizardBack}
              onNext={handleWizardNext}
              onSaveDraft={handleWizardSaveDraft}
              onClose={handleWizardRequestClose}
              primaryLabel={
                wizardStep === 0
                  ? 'Continue to Select Products'
                  : wizardStep === 1
                    ? 'Continue to Configure'
                    : wizardStep === 2
                      ? 'Continue to Preview'
                      : 'Publish Package'
              }
            >
              {wizardStep === 0 ? (
                <VStack align="stretch" spacing={4}>
                  <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
                    <FormControl isRequired>
                      <FormLabel>Package Name</FormLabel>
                      <Input
                        value={wizardForm.package_name}
                        onChange={(e) => handleWizardFormChange('package_name', e.target.value)}
                        placeholder="Masukkan package name"
                      />
                    </FormControl>
                    <FormControl>
                      <FormLabel>PIC Member</FormLabel>
                      <Select
                        value={wizardForm.pic_member}
                        onChange={(e) => handleWizardFormChange('pic_member', e.target.value)}
                        placeholder="Pilih PIC Member"
                      >
                        {Object.entries(MEMBER_CODE_MAPPING).map(([code, name]) => (
                          <option key={code} value={code}>
                            {name}
                          </option>
                        ))}
                      </Select>
                    </FormControl>
                  </SimpleGrid>
                  <FormControl>
                    <FormLabel>Description</FormLabel>
                    <Textarea
                      value={wizardForm.description}
                      onChange={(e) => handleWizardFormChange('description', e.target.value)}
                      placeholder={'Masukkan description.\n\n**Promo spesial**\n- Poin pertama\n- Poin kedua'}
                      rows={5}
                    />
                    <Text fontSize="xs" color="gray.500" mt={1}>{MD_HINT}</Text>
                    {wizardForm.description?.trim() && (
                      <Box mt={2} p={3} borderWidth="1px" borderRadius="md" bg="gray.50">
                        <Text fontSize="xs" color="gray.400" mb={1}>Preview</Text>
                        <MarkdownText>{wizardForm.description}</MarkdownText>
                      </Box>
                    )}
                  </FormControl>
                  <FormControl>
                    <FormLabel>Price Includes</FormLabel>
                    <Textarea
                      value={wizardForm.price_includes}
                      onChange={(e) => handleWizardFormChange('price_includes', e.target.value)}
                      placeholder="Contoh: Tiket masuk, welcome drink"
                      rows={3}
                    />
                  </FormControl>
                  <FormControl>
                    <FormLabel>Price Excludes</FormLabel>
                    <Textarea
                      value={wizardForm.price_excludes}
                      onChange={(e) => handleWizardFormChange('price_excludes', e.target.value)}
                      placeholder="Contoh: Transportasi, oleh-oleh"
                      rows={3}
                    />
                  </FormControl>
                  <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
                    <FormControl>
                      <FormLabel>Start Date</FormLabel>
                      <Input
                        type="date"
                        value={wizardForm.valid_date}
                        onChange={(e) => handleWizardFormChange('valid_date', e.target.value)}
                      />
                    </FormControl>
                    <FormControl>
                      <FormLabel>End Date</FormLabel>
                      <Input
                        type="date"
                        value={wizardForm.valid_end_date}
                        min={wizardForm.valid_date || ''}
                        onChange={(e) => handleWizardFormChange('valid_end_date', e.target.value)}
                      />
                    </FormControl>
                  </SimpleGrid>
                  <FormControl>
                    <FormLabel>Kuota</FormLabel>
                    <NumberInput
                      value={wizardForm.quota}
                      min={0}
                      onChange={(valueString) => handleWizardFormChange('quota', valueString)}
                    >
                      <NumberInputField placeholder="Masukkan kuota" />
                      <NumberInputStepper>
                        <NumberIncrementStepper />
                        <NumberDecrementStepper />
                      </NumberInputStepper>
                    </NumberInput>
                  </FormControl>
                </VStack>
              ) : wizardStep === 1 ? (
                <SimpleGrid columns={{ base: 1, xl: 2 }} spacing={5}>
                  <Card bg="surface.tertiary" borderRadius="md" border="0.5px solid" borderColor="rgba(15, 23, 42, 0.10)" boxShadow="none">
                    <CardBody>
                      <VStack align="stretch" spacing={3}>
                        <Text fontSize="sm" fontWeight="700" color="label.primary">
                          Product Catalog
                        </Text>
                        <InputGroup>
                          <InputLeftElement pointerEvents="none">
                            <Icon as={FiSearch} color="gray.400" />
                          </InputLeftElement>
                          <Input
                            placeholder="Cari product name, code, atau source..."
                            value={wizardSearchQuery}
                            onChange={(e) => setWizardSearchQuery(e.target.value)}
                          />
                        </InputGroup>
                        <Box maxH="360px" overflowY="auto" pr={1}>
                          <VStack align="stretch" spacing={2}>
                            {wizardLoadingInventory ? (
                              <Text fontSize="sm" color="label.secondary">Memuat product catalog...</Text>
                            ) : wizardInventoryItems.length === 0 ? (
                              <Text fontSize="sm" color="label.secondary">Tidak ada product yang cocok.</Text>
                            ) : (
                              wizardInventoryItems.map((item) => (
                                <Card
                                  key={item.id}
                                  bg={wizardSelectedInventoryItem?.id === item.id ? 'accent.blueSubtle' : 'white'}
                                  border="0.5px solid"
                                  borderColor={wizardSelectedInventoryItem?.id === item.id ? 'accent.blueBorder' : 'gray.200'}
                                  boxShadow="none"
                                  cursor="pointer"
                                  onClick={() => handleWizardSelectInventoryItem(item)}
                                >
                                  <CardBody py={3}>
                                    <VStack align="stretch" spacing={2}>
                                      <HStack justify="space-between" align="start">
                                        <VStack align="start" spacing={0.5}>
                                          <Text fontSize="sm" fontWeight="700" color="label.primary">
                                            {item.name}
                                          </Text>
                                          <Text fontSize="xs" color="label.secondary">
                                            {item.productCode}
                                          </Text>
                                        </VStack>
                                        <DomainBadge
                                          domain={item.memberCode}
                                          label={MEMBER_CODE_MAPPING[item.memberCode?.toLowerCase()] || item.location}
                                        />
                                      </HStack>
                                      <HStack justify="space-between" mt={1}>
                                        <Text fontSize="xs" color="label.secondary" noOfLines={2}>
                                          {item.description}
                                        </Text>
                                        <Badge
                                          colorScheme={item.apiStatus === 'published' ? 'green' : 'gray'}
                                          fontSize="2xs"
                                          flexShrink={0}
                                          ml={2}
                                        >
                                          {item.apiStatus === 'published' ? 'Published' : 'Draft'}
                                        </Badge>
                                      </HStack>
                                    </VStack>
                                  </CardBody>
                                </Card>
                              ))
                            )}
                          </VStack>
                        </Box>
                      </VStack>
                    </CardBody>
                  </Card>

                  <Card bg="surface.tertiary" borderRadius="md" border="0.5px solid" borderColor="rgba(15, 23, 42, 0.10)" boxShadow="none">
                    <CardBody>
                      <VStack align="stretch" spacing={3}>
                        <Text fontSize="sm" fontWeight="700" color="label.primary">
                          Selected Panel ({wizardSelectedItems.length})
                        </Text>
                        {wizardSelectedInventoryItem && (
                          <Card bg="accent.blueSubtle" border="0.5px solid" borderColor="accent.blueBorder" boxShadow="none">
                            <CardBody>
                              <VStack align="stretch" spacing={2}>
                                <Text fontSize="sm" fontWeight="700">{wizardSelectedInventoryItem.name}</Text>
                                <SimpleGrid columns={{ base: 1, md: 2 }} spacing={2}>
                                  <FormControl>
                                    <FormLabel fontSize="xs">Quantity</FormLabel>
                                    <NumberInput value={wizardAddQuantity} min={1} max={wizardSelectedInventoryItem.quantity || 999} onChange={(valueString) => setWizardAddQuantity(parseInt(valueString, 10) || 1)}>
                                      <NumberInputField />
                                      <NumberInputStepper>
                                        <NumberIncrementStepper />
                                        <NumberDecrementStepper />
                                      </NumberInputStepper>
                                    </NumberInput>
                                  </FormControl>
                                  {wizardSelectedInventoryItem.productType !== 'retail' && (
                                    <FormControl>
                                      <FormLabel fontSize="xs">Price Type</FormLabel>
                                      <Select value={wizardAddSelectedPriceType} onChange={(e) => setWizardAddSelectedPriceType(e.target.value)}>
                                        <option value="base">Publish Price</option>
                                        {wizardSelectedInventoryItem.corporatePrice ? <option value="corporate">Corporate Price</option> : null}
                                        {wizardSelectedInventoryItem.agentPrice ? <option value="agent">Ecosystem Price</option> : null}
                                      </Select>
                                    </FormControl>
                                  )}
                                </SimpleGrid>
                                <HStack justify="space-between" fontSize="xs">
                                  <Text color="label.secondary">Selected price</Text>
                                  <Text fontWeight="700" className="tabular-nums">
                                    Rp {getWizardSelectedPrice(wizardSelectedInventoryItem).toLocaleString('id-ID')}
                                  </Text>
                                </HStack>
                                <Button colorScheme="brand" size="sm" leftIcon={<FiPlus />} onClick={handleWizardAddProduct} isLoading={wizardSaving}>
                                  Add to Selected Panel
                                </Button>
                              </VStack>
                            </CardBody>
                          </Card>
                        )}
                        <Box maxH="400px" overflowY="auto" pr={1}>
                          <VStack align="stretch" spacing={2}>
                            {wizardSelectedItems.map((item) => {
                              const name = item.name || item.product?.BPPRODUCT_NAME || 'Product';
                              const code = item.product?.BPPRODUCT_CODE || '-';
                              const sourceMember = item.product?.MEMBER_CODE || item.memberCode;
                              const { quantity, priceType, finalPrice, subtotal } = getPriceForItem(item);
                              const priceLabel = priceType === 'corporate' ? 'Corporate' : priceType === 'agent' ? 'Ecosystem' : 'Publish';
                              return (
                                <Card key={item.PACKAGEDETAIL_ID || `${name}-${code}`} boxShadow="none" border="0.5px solid" borderColor="gray.200">
                                  <CardBody py={2.5}>
                                    <VStack align="stretch" spacing={1.5}>
                                      <HStack justify="space-between" align="start">
                                        <VStack align="start" spacing={0.5} flex={1}>
                                          <Text fontSize="sm" fontWeight="700">{name}</Text>
                                          <Text fontSize="xs" color="label.secondary">{code}</Text>
                                        </VStack>
                                        <Button size="xs" colorScheme="red" variant="ghost" onClick={() => handleWizardRemoveProduct(item)} isLoading={wizardSaving}>
                                          Remove
                                        </Button>
                                      </HStack>
                                      <SimpleGrid columns={4} spacing={2} pt={1} borderTop="0.5px solid" borderColor="gray.100">
                                        <VStack align="start" spacing={0}>
                                          <Text fontSize="2xs" color="label.tertiary">Qty</Text>
                                          <Text fontSize="xs" fontWeight="600">{quantity}</Text>
                                        </VStack>
                                        <VStack align="start" spacing={0}>
                                          <Text fontSize="2xs" color="label.tertiary">Price Type</Text>
                                          <Badge fontSize="2xs" colorScheme={priceType === 'corporate' ? 'green' : priceType === 'agent' ? 'purple' : 'blue'}>
                                            {priceLabel}
                                          </Badge>
                                        </VStack>
                                        <VStack align="start" spacing={0}>
                                          <Text fontSize="2xs" color="label.tertiary">Unit Price</Text>
                                          <Text fontSize="xs" fontWeight="600">Rp {finalPrice.toLocaleString('id-ID')}</Text>
                                        </VStack>
                                        <VStack align="start" spacing={0}>
                                          <Text fontSize="2xs" color="label.tertiary">Subtotal</Text>
                                          <Text fontSize="xs" fontWeight="700" color="brand.600">Rp {subtotal.toLocaleString('id-ID')}</Text>
                                        </VStack>
                                      </SimpleGrid>
                                      <DomainBadge domain={sourceMember} label={MEMBER_CODE_MAPPING[sourceMember?.toLowerCase()] || sourceMember || 'Unknown source'} />
                                    </VStack>
                                  </CardBody>
                                </Card>
                              );
                            })}
                            {wizardSelectedItems.length === 0 && (
                              <Text fontSize="sm" color="label.secondary">
                                Belum ada product di selected panel.
                              </Text>
                            )}
                          </VStack>
                        </Box>
                      </VStack>
                    </CardBody>
                  </Card>
                </SimpleGrid>
              ) : wizardStep === 2 ? (
                <VStack align="stretch" spacing={4}>
                  <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
                    <FormControl>
                      <FormLabel>Pricing Mode</FormLabel>
                      <Select
                        value={wizardConfig.pricingMode}
                        onChange={(e) => handleWizardConfigChange({ pricingMode: e.target.value })}
                      >
                        <option value="inherit">Inherit product pricing</option>
                        <option value="manual-discount-cap">Manual with discount cap</option>
                      </Select>
                    </FormControl>
                    <FormControl>
                      <FormLabel>Discount Cap (%)</FormLabel>
                      <NumberInput
                        value={wizardConfig.discountCapPercent}
                        min={0}
                        max={100}
                        onChange={(valueString) =>
                          handleWizardConfigChange({ discountCapPercent: parseInt(valueString, 10) || 0 })
                        }
                      >
                        <NumberInputField />
                        <NumberInputStepper>
                          <NumberIncrementStepper />
                          <NumberDecrementStepper />
                        </NumberInputStepper>
                      </NumberInput>
                    </FormControl>
                  </SimpleGrid>

                  <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
                    <FormControl>
                      <FormLabel>Channel Hint</FormLabel>
                      <Select
                        value={wizardConfig.channelHint}
                        onChange={(e) => handleWizardConfigChange({ channelHint: e.target.value })}
                      >
                        <option value="all-channels">All Channels</option>
                        <option value="retail-priority">Retail Priority</option>
                      </Select>
                    </FormControl>
                    <FormControl>
                      <FormLabel>Validity and Quota</FormLabel>
                      <Card boxShadow="none" border="0.5px solid" borderColor="gray.200">
                        <CardBody py={3}>
                          <VStack align="stretch" spacing={1}>
                            <Text fontSize="xs" color="label.secondary">
                              Validity: {wizardForm.valid_date || '-'} to {wizardForm.valid_end_date || '-'}
                            </Text>
                            <Text fontSize="xs" color="label.secondary">
                              Quota: {wizardForm.quota || 0}
                            </Text>
                            <Text fontSize="xs" color="label.tertiary">
                              Ubah detail ini di Step 1 Basics.
                            </Text>
                          </VStack>
                        </CardBody>
                      </Card>
                    </FormControl>
                  </SimpleGrid>

                  <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
                    <FormControl>
                      <FormLabel>Channel Distribution</FormLabel>
                      <Card boxShadow="none" border="0.5px solid" borderColor="gray.200">
                        <CardBody py={3}>
                          <VStack align="stretch" spacing={2}>
                            <HStack justify="space-between">
                              <Text fontSize="xs" color="label.secondary">Target channel:</Text>
                              <Badge colorScheme="blue" fontSize="2xs">
                                {wizardConfig.channelHint === 'all-channels' ? 'All Channels' : 'Retail Priority'}
                              </Badge>
                            </HStack>
                            <HStack justify="space-between">
                              <Text fontSize="xs" color="label.secondary">PIC Member:</Text>
                              <Text fontSize="xs" fontWeight="600">
                                {MEMBER_CODE_MAPPING[wizardConfig.pricingMode] || wizardForm.pic_member || '-'}
                              </Text>
                            </HStack>
                            <Text fontSize="2xs" color="label.tertiary" mt={1}>
                              Koneksi channel dikelola di tab Integrasi API Mitra. Publish akan mendistribusikan paket ke channel yang terdaftar.
                            </Text>
                          </VStack>
                        </CardBody>
                      </Card>
                    </FormControl>
                    <FormControl>
                      <FormLabel>Notes</FormLabel>
                      <Textarea
                        value={wizardConfig.notes}
                        onChange={(e) => handleWizardConfigChange({ notes: e.target.value })}
                        placeholder="Catat hint channel, media note, atau constraints bisnis."
                        rows={4}
                      />
                    </FormControl>
                  </SimpleGrid>
                </VStack>
              ) : (
                <VStack align="stretch" spacing={4}>
                  <SimpleGrid columns={{ base: 1, xl: 2 }} spacing={4}>
                    <Card boxShadow="none" border="0.5px solid" borderColor="gray.200">
                      <CardBody>
                        <VStack align="stretch" spacing={3}>
                          <Text fontSize="sm" fontWeight="700" color="label.primary">
                            Preview Summary
                          </Text>
                          <SimpleGrid columns={2} spacing={2}>
                            <Text fontSize="xs" color="label.secondary">Package Name</Text>
                            <Text fontSize="xs" fontWeight="700" textAlign="right">{wizardForm.package_name || '-'}</Text>
                            <Text fontSize="xs" color="label.secondary">PIC Member</Text>
                            <Text fontSize="xs" fontWeight="700" textAlign="right">
                              {MEMBER_CODE_MAPPING[wizardForm.pic_member?.toLowerCase()] || wizardForm.pic_member || '-'}
                            </Text>
                            <Text fontSize="xs" color="label.secondary">Period</Text>
                            <Text fontSize="xs" fontWeight="700" textAlign="right">
                              {wizardForm.valid_date || '-'} to {wizardForm.valid_end_date || '-'}
                            </Text>
                            <Text fontSize="xs" color="label.secondary">Quota</Text>
                            <Text fontSize="xs" fontWeight="700" textAlign="right" className="tabular-nums">
                              {Number(wizardForm.quota || 0).toLocaleString('id-ID')}
                            </Text>
                          </SimpleGrid>
                          <PriceSummaryBlock
                            totalBasePrice={getWizardPreviewSummary().totalBasePrice}
                            totalFinalPrice={getWizardPreviewSummary().totalFinalPrice}
                          />
                        </VStack>
                      </CardBody>
                    </Card>
                    <Card boxShadow="none" border="0.5px solid" borderColor="gray.200">
                      <CardBody>
                        <VStack align="stretch" spacing={3}>
                          <Text fontSize="sm" fontWeight="700" color="label.primary">
                            Publish Gate
                          </Text>
                          <ReadinessPanel blockers={getWizardPublishBlockers()} />
                          <Text fontSize="xs" color="label.secondary" lineHeight="1.7">
                            Publish blocker menampilkan cause dan recovery path. Wizard hanya mengizinkan publish jika blocker = 0.
                          </Text>
                        </VStack>
                      </CardBody>
                    </Card>
                  </SimpleGrid>
                  <Box>
                    <Text fontSize="sm" fontWeight="700" color="label.primary" mb={2}>
                      Selected Products ({wizardSelectedItems.length})
                    </Text>
                    <VStack align="stretch" spacing={2} maxH="220px" overflowY="auto">
                      {wizardSelectedItems.map((item) => {
                        const name = item.name || item.product?.BPPRODUCT_NAME || 'Product';
                        const code = item.product?.BPPRODUCT_CODE || '-';
                        const sourceMember = item.product?.MEMBER_CODE || item.memberCode;
                        return (
                          <Card key={item.PACKAGEDETAIL_ID || `${name}-${code}`} boxShadow="none" border="0.5px solid" borderColor="gray.200">
                            <CardBody py={2.5}>
                              <HStack justify="space-between" align="center">
                                <VStack align="start" spacing={0.5}>
                                  <Text fontSize="sm" fontWeight="700">{name}</Text>
                                  <Text fontSize="xs" color="label.secondary">{code}</Text>
                                </VStack>
                                <DomainBadge domain={sourceMember} label={MEMBER_CODE_MAPPING[sourceMember?.toLowerCase()] || sourceMember || 'Unknown source'} />
                              </HStack>
                            </CardBody>
                          </Card>
                        );
                      })}
                    </VStack>
                  </Box>
                </VStack>
              )}
            </WizardShell>
          </ModalBody>
        </ModalContent>
      </Modal>

      <AlertDialog
        isOpen={isWizardExitOpen}
        leastDestructiveRef={wizardExitCancelRef}
        onClose={onWizardExitClose}
      >
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader fontSize="lg" fontWeight="bold">
              Exit Wizard
            </AlertDialogHeader>
            <AlertDialogBody>
              Perubahan belum tersimpan sepenuhnya. Simpan draft dulu sebelum keluar?
            </AlertDialogBody>
            <AlertDialogFooter>
              <Button ref={wizardExitCancelRef} onClick={onWizardExitClose}>
                Kembali
              </Button>
              <Button
                colorScheme="blue"
                variant="outline"
                onClick={async () => {
                  await handleWizardSaveDraft();
                  await closeWizardSafely();
                }}
                ml={3}
              >
                Save Draft & Exit
              </Button>
              <Button colorScheme="red" onClick={closeWizardSafely} ml={3}>
                Exit Without Saving
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>

      {/* Edit Item Modal */}
      <Modal isOpen={isItemEditOpen} onClose={onItemEditClose} size="md">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Edit Item</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            {editingItem && (
              <VStack spacing={4}>
                <Card bg="gray.50" p={4} w="100%">
                  <Text fontWeight="600" fontSize="md">{editingItem.name}</Text>
                  <Text fontSize="sm" color="gray.600">{editingItem.location}</Text>
                </Card>

                <FormControl>
                  <FormLabel>Quantity</FormLabel>
                  <NumberInput
                    value={itemFormData.quantity}
                    onChange={(valueString) => setItemFormData({ ...itemFormData, quantity: parseInt(valueString) || 1 })}
                    min={1}
                  >
                    <NumberInputField />
                    <NumberInputStepper>
                      <NumberIncrementStepper />
                      <NumberDecrementStepper />
                    </NumberInputStepper>
                  </NumberInput>
                </FormControl>

                <FormControl>
                  <FormLabel>Base Price (Harga Asli)</FormLabel>
                  <Input
                    value={`Rp ${(editingItem.base_price || editingItem.unit_price || 0).toLocaleString('id-ID')}`}
                    isReadOnly
                    bg="gray.50"
                  />
                </FormControl>

                <FormControl>
                  <FormLabel>Pricing Type</FormLabel>
                  <Select
                    value={itemPriceType}
                    onChange={(e) => {
                      setItemPriceType(e.target.value);
                      const basePrice = editingItem.base_price || editingItem.unit_price || 0;
                      if (e.target.value === 'bundle') {
                        setItemFormData({ ...itemFormData, unit_price: basePrice });
                        setItemDiscountPercent(0);
                      } else {
                        setItemFormData({ ...itemFormData, unit_price: basePrice });
                        setItemDiscountPercent(0);
                      }
                    }}
                  >
                    <option value="bundle">Bundle Price</option>
                    <option value="discount">Discount (%)</option>
                  </Select>
                </FormControl>

                {itemPriceType === 'bundle' ? (
                  <FormControl>
                    <FormLabel>Bundle Price (Rp)</FormLabel>
                    <NumberInput
                      value={itemFormData.unit_price}
                      onChange={(valueString) => setItemFormData({ ...itemFormData, unit_price: parseFloat(valueString) || 0 })}
                      min={0}
                      precision={0}
                    >
                      <NumberInputField />
                      <NumberInputStepper>
                        <NumberIncrementStepper />
                        <NumberDecrementStepper />
                      </NumberInputStepper>
                    </NumberInput>
                    <Text fontSize="xs" color="gray.500" mt={1}>
                      Total: Rp {(itemFormData.quantity * itemFormData.unit_price).toLocaleString('id-ID')}
                    </Text>
                  </FormControl>
                ) : (
                  <FormControl>
                    <FormLabel>Discount (%)</FormLabel>
                    <NumberInput
                      value={itemDiscountPercent}
                      onChange={(valueString) => {
                        const discount = parseFloat(valueString) || 0;
                        setItemDiscountPercent(discount);
                        const basePrice = editingItem.base_price || editingItem.unit_price || 0;
                        const discountAmount = (basePrice * discount) / 100;
                        setItemFormData({ ...itemFormData, unit_price: basePrice - discountAmount });
                      }}
                      min={0}
                      max={100}
                      precision={2}
                    >
                      <NumberInputField />
                      <NumberInputStepper>
                        <NumberIncrementStepper />
                        <NumberDecrementStepper />
                      </NumberInputStepper>
                    </NumberInput>
                    <VStack align="stretch" spacing={1} mt={2}>
                      <HStack justify="space-between" fontSize="xs">
                        <Text color="gray.500">Discount Amount:</Text>
                        <Text fontWeight="600" color="red.500">
                          - Rp {((editingItem.base_price || editingItem.unit_price || 0) * itemDiscountPercent / 100).toLocaleString('id-ID')}
                        </Text>
                      </HStack>
                      <HStack justify="space-between" fontSize="xs">
                        <Text color="gray.500">Final Price:</Text>
                        <Text fontWeight="600" color="brand.600">
                          Rp {itemFormData.unit_price.toLocaleString('id-ID')}
                        </Text>
                      </HStack>
                      <HStack justify="space-between" fontSize="xs" pt={1} borderTop="1px solid" borderColor="gray.200">
                        <Text color="gray.700" fontWeight="600">Total:</Text>
                        <Text fontWeight="700" color="brand.600" fontSize="sm">
                          Rp {(itemFormData.quantity * itemFormData.unit_price).toLocaleString('id-ID')}
                        </Text>
                      </HStack>
                    </VStack>
                  </FormControl>
                )}
              </VStack>
            )}
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={onItemEditClose}>
              Cancel
            </Button>
            <Button colorScheme="brand" onClick={handleSaveItemEdit}>
              Save Changes
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Add Item from Inventory Modal */}
      <Modal isOpen={isAddItemOpen} onClose={onAddItemClose} size="xl">
        <ModalOverlay />
        <ModalContent maxH="90vh" overflowY="auto">
          <ModalHeader>Add Item from Inventory</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack spacing={4} align="stretch">
              {/* Search */}
              <InputGroup>
                <InputLeftElement pointerEvents="none">
                  <Icon as={FiSearch} color="gray.400" />
                </InputLeftElement>
                <Input
                  placeholder="Cari item (nama, deskripsi, lokasi)..."
                  value={searchInventoryQuery}
                  onChange={(e) => setSearchInventoryQuery(e.target.value)}
                />
              </InputGroup>

              {/* Selected Item Info */}
              {selectedInventoryItem && (
                <Card bg="brand.50" p={4}>
                  <VStack align="stretch" spacing={2}>
                    <Text fontWeight="600" fontSize="md">{selectedInventoryItem.name}</Text>
                    <Text fontSize="sm" color="gray.600">{selectedInventoryItem.description}</Text>
                    <HStack justify="space-between">
                      <Text fontSize="sm" color="gray.500">Location:</Text>
                      <Text fontSize="sm" fontWeight="600">{selectedInventoryItem.location}</Text>
                    </HStack>
                    <HStack justify="space-between">
                      <Text fontSize="sm" color="gray.500">Available:</Text>
                      <Text fontSize="sm" fontWeight="600">{selectedInventoryItem.quantity}</Text>
                    </HStack>
                  </VStack>
                </Card>
              )}

              {/* Quantity and Price Inputs */}
              {selectedInventoryItem && (
                <>
                  <FormControl>
                    <FormLabel>Quantity</FormLabel>
                    <NumberInput
                      value={addItemQuantity}
                      onChange={(valueString) => setAddItemQuantity(parseInt(valueString) || 1)}
                      min={1}
                      max={selectedInventoryItem.quantity || 999}
                    >
                      <NumberInputField />
                      <NumberInputStepper>
                        <NumberIncrementStepper />
                        <NumberDecrementStepper />
                      </NumberInputStepper>
                    </NumberInput>
                  </FormControl>

                  {/* Price Information - Different for retail vs non-retail */}
                  {selectedInventoryItem.productType === 'retail' ? (
                    <>
                      <FormControl>
                        <FormLabel>Base Price (Harga Asli)</FormLabel>
                        <Input
                          value={`Rp ${(selectedInventoryItem.price || selectedInventoryItem.originalPrice || 0).toLocaleString('id-ID')}`}
                          isReadOnly
                          bg="gray.50"
                        />
                      </FormControl>

                      <FormControl>
                        <FormLabel>Bundle Price (Rp)</FormLabel>
                        <NumberInput
                          value={addItemPrice}
                          onChange={(valueString) => setAddItemPrice(parseFloat(valueString) || 0)}
                          min={0}
                          precision={0}
                        >
                          <NumberInputField />
                          <NumberInputStepper>
                            <NumberIncrementStepper />
                            <NumberDecrementStepper />
                          </NumberInputStepper>
                        </NumberInput>
                        <Text fontSize="xs" color="gray.500" mt={1}>
                          Total: Rp {(addItemQuantity * addItemPrice).toLocaleString('id-ID')}
                        </Text>
                      </FormControl>
                    </>
                  ) : (
                    <>

                      {selectedInventoryItem.corporatePrice && (
                        <FormControl>
                          <FormLabel>Corporate Price</FormLabel>
                          <HStack spacing={2}>
                            <Input
                              value={`Rp ${selectedInventoryItem.corporatePrice.toLocaleString('id-ID')}`}
                              isReadOnly
                              bg="gray.50"
                              flex={1}
                            />
                            <Button
                              size="sm"
                              colorScheme={addItemSelectedPriceType === 'corporate' ? 'blue' : 'gray'}
                              variant={addItemSelectedPriceType === 'corporate' ? 'solid' : 'outline'}
                              onClick={() => handleSelectPriceType('corporate', selectedInventoryItem)}
                            >
                              Pilih
                            </Button>
                          </HStack>
                        </FormControl>
                      )}

                      {selectedInventoryItem.agentPrice && (
                        <FormControl>
                          <FormLabel>Ecosystem Price</FormLabel>
                          <HStack spacing={2}>
                            <Input
                              value={`Rp ${selectedInventoryItem.agentPrice.toLocaleString('id-ID')}`}
                              isReadOnly
                              bg="gray.50"
                              flex={1}
                            />
                            <Button
                              size="sm"
                              colorScheme={addItemSelectedPriceType === 'agent' ? 'blue' : 'gray'}
                              variant={addItemSelectedPriceType === 'agent' ? 'solid' : 'outline'}
                              onClick={() => handleSelectPriceType('agent', selectedInventoryItem)}
                            >
                              Pilih
                            </Button>
                          </HStack>
                        </FormControl>
                      )}

                      <FormControl>
                        <FormLabel>Publish Price (Base)</FormLabel>
                        <HStack spacing={2}>
                          <Input
                            value={`Rp ${(selectedInventoryItem.price || selectedInventoryItem.originalPrice || 0).toLocaleString('id-ID')}`}
                            isReadOnly
                            bg="gray.50"
                            flex={1}
                          />
                          <Button
                            size="sm"
                            colorScheme={addItemSelectedPriceType === 'base' ? 'blue' : 'gray'}
                            variant={addItemSelectedPriceType === 'base' ? 'solid' : 'outline'}
                            onClick={() => handleSelectPriceType('base', selectedInventoryItem)}
                          >
                            Pilih
                          </Button>
                        </HStack>
                      </FormControl>

                      <FormControl>
                        <FormLabel>Selected Price</FormLabel>
                        <Input
                          value={`Rp ${addItemPrice.toLocaleString('id-ID')}`}
                          isReadOnly
                          bg="blue.50"
                          borderColor="blue.300"
                        />
                        <Text fontSize="xs" color="gray.500" mt={1}>
                          Price Type: {addItemSelectedPriceType === 'base' ? 'Publish Price' : addItemSelectedPriceType === 'corporate' ? 'Corporate Price' : 'Ecosystem Price'}
                        </Text>
                        <Text fontSize="xs" color="gray.500" mt={1}>
                          Total: Rp {(addItemQuantity * addItemPrice).toLocaleString('id-ID')}
                        </Text>
                      </FormControl>
                    </>
                  )}
                </>
              )}

              <Divider />

              {/* Inventory Items List */}
              <Text fontSize="sm" fontWeight="600" color="gray.600">
                Available Items ({filteredInventoryItems.length}):
              </Text>
              <VStack align="stretch" spacing={2} maxH="300px" overflowY="auto">
                {filteredInventoryItems.map((item) => (
                  <Card
                    key={item.id}
                    bg={selectedInventoryItem?.id === item.id ? 'brand.50' : 'white'}
                    border={selectedInventoryItem?.id === item.id ? '2px solid' : '1px solid'}
                    borderColor={selectedInventoryItem?.id === item.id ? 'brand.500' : 'gray.200'}
                    p={3}
                    cursor="pointer"
                    onClick={() => handleSelectInventoryItem(item)}
                    _hover={{ borderColor: 'brand.400', boxShadow: 'sm' }}
                  >
                    <VStack align="stretch" spacing={1}>
                      <Text fontSize="sm" fontWeight="600" noOfLines={1}>
                        {item.name}
                      </Text>
                      <Text fontSize="xs" color="gray.600" noOfLines={1}>
                        {item.description}
                      </Text>
                      <HStack justify="space-between" fontSize="xs">
                        <Text color="gray.500">{item.location}</Text>
                        <Text color="brand.600" fontWeight="600">
                          Rp {item.price.toLocaleString('id-ID')}
                        </Text>
                      </HStack>
                    </VStack>
                  </Card>
                ))}
              </VStack>
            </VStack>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={onAddItemClose}>
              Cancel
            </Button>
            <Button
              colorScheme="brand"
              onClick={handleAddItemToPackage}
              isDisabled={!selectedInventoryItem}
            >
              Add to Package
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Delete Item Confirmation */}
      <AlertDialog
        isOpen={isDeleteItemOpen}
        leastDestructiveRef={cancelRef}
        onClose={onDeleteItemClose}
      >
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader fontSize="lg" fontWeight="bold">
              Delete Item
            </AlertDialogHeader>

            <AlertDialogBody>
              {deleteItem ? (
                <>
                  Apakah Anda yakin ingin menghapus item berikut dari package?
                  <VStack align="start" spacing={1} mt={3} p={3} bg="gray.50" borderRadius="md">
                    <Text fontWeight="600" fontSize="sm">
                      {deleteItem.name || deleteItem.product?.BPPRODUCT_NAME || deleteItem.product?.name || 'Product'}
                    </Text>
                    <Text fontSize="xs" color="gray.600">
                      Code: {deleteItem.product?.BPPRODUCT_CODE || deleteItem.product?.productCode || '-'}
                    </Text>
                    {(deleteItem.quantity || deleteItem.PACKAGEDETAIL_QUANTITY) && (
                      <Text fontSize="xs" color="gray.600">
                        Quantity: {deleteItem.quantity || deleteItem.PACKAGEDETAIL_QUANTITY || 1}
                      </Text>
                    )}
                  </VStack>
                </>
              ) : (
                'Apakah Anda yakin ingin menghapus item ini dari package?'
              )}
            </AlertDialogBody>

            <AlertDialogFooter>
              <Button ref={cancelRef} onClick={onDeleteItemClose}>
                Cancel
              </Button>
              <Button colorScheme="red" onClick={handleDeleteItem} ml={3}>
                Delete
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>

      {/* Manage Images Modal */}
      <Modal isOpen={isManageImagesOpen} onClose={onManageImagesClose} size="xl">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Manage Package Images</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack spacing={4} align="stretch">
              <Box>
                <FormLabel>Upload Images</FormLabel>
                <Input
                  type="file"
                  accept="image/*"
                  multiple
                  onChange={handleImageUpload}
                  disabled={uploadingImages}
                />
                {uploadingImages && (
                  <Text fontSize="sm" color="blue.500" mt={2}>
                    Uploading images...
                  </Text>
                )}
              </Box>

              <Divider />

              <Text fontSize="sm" fontWeight="600">
                Package Images ({packageImages.length})
              </Text>

              {packageImages.length > 0 ? (
                <SimpleGrid columns={{ base: 2, md: 3 }} spacing={4}>
                  {packageImages.map((image) => (
                    <Box key={image.PACKAGEIMAGE_ID} position="relative">
                      <Box
                        as="img"
                        src={image.PACKAGEIMAGE_FILE ? (image.PACKAGEIMAGE_FILE.startsWith('http') ? image.PACKAGEIMAGE_FILE : `/api-integrated/images/packages/${image.PACKAGEIMAGE_FILE}`) : ''}
                        alt={image.PACKAGEIMAGE_NAME || 'Package Image'}
                        w="100%"
                        h="200px"
                        objectFit="cover"
                        borderRadius="md"
                        border="1px solid"
                        borderColor="gray.200"
                      />
                      <IconButton
                        icon={<FiTrash2 />}
                        size="sm"
                        colorScheme="red"
                        variant="solid"
                        position="absolute"
                        top={2}
                        right={2}
                        onClick={() => handleDeleteImage(image.PACKAGEIMAGE_ID)}
                        aria-label="Delete Image"
                      />
                    </Box>
                  ))}
                </SimpleGrid>
              ) : (
                <Text fontSize="sm" color="gray.500" textAlign="center" py={4}>
                  No images uploaded yet
                </Text>
              )}
            </VStack>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" onClick={onManageImagesClose}>
              Close
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
};

export default MyBundlingPackage;


